import React, {FC, useEffect, useState, useMemo, useRef} from 'react'
import {KTSVG} from '../../../../../_metronic/helpers'
import {useAuth} from '../../../auth'
import {useLocation} from 'react-router-dom'
import * as XLSX from 'xlsx'
import {useGlobalFilter} from '../../../../shared/context/GlobalFilterContext'
import {
  getCompanyAdminList,
  getCompanyList,
  getVesselList,
} from '../../../Management/core/_requests'
import { useAccessibleVessels } from '../../../../shared/access/accessibleVessels'
import { getAccessibleVessels } from '../../core/technicalScopeUtils'
import {toast, ToastContainer} from 'react-toastify'
import type {Vessel} from '../../../Management/core/_models'
import type {
  PagedResult,
  PmsJobCombinedDto,
  PmsJobRecord,
  PmsJobState,
  PmsPlanDto,
  PmsPlanLineDto,
} from '../../core/pms/_models'
import {
  searchPmsJobsCombined,
  searchPmsJobs,
  getPmsJobSummaryStats,
  getPmsJobDisplay,
  getJobRequestsPage,
  acceptJobRequest,
  rejectJobRequest,
  forceOverduePlanLineJob,
  getPmsPlans,
  getPmsPlanLines,
  createPmsPlan,
  deletePmsPlanLine,
} from '../../core/pms/_requests'
import type {QHSEDefectDto} from '../../../QHSE/core/_models'
import {getDefect} from '../../../QHSE/core/_requests'
import {PmsJobDetailModal} from './PmsJobDetailModal'
import {CompleteJobModal} from './CompleteJobModal'
import {CreateUnplannedJobModal} from './CreateUnplannedJobModal'
import {BatchJobActionModal} from './BatchJobActionModal'
import {DeferJobModal} from './DeferJobModal'
import {PmsJobHistoryModal} from './PmsJobHistoryModal'
import {AcceptJobRequestModal} from './AcceptJobRequestModal'
import {RejectJobRequestModal} from './RejectJobRequestModal'
import {ViewDefectModal} from '../../../QHSE/components/ViewDefectModal'
import {AddPlanModal} from './AddPlanModal'
import {AddPlanLineModal} from './AddPlanLineModal'
import {EditPlanLineModal} from './EditPlanLineModal'
import {JobMaskingModal} from './JobMaskingModal'
import {TECH_PERM} from '../../core/technicalPermissions'
import {
  formatJobDate,
  getDisplayedJobState,
  getDisplayedJobStateBadgeColor,
  getPlannedJobReasonLabel,
  getPlannedTriggerDescription,
  getJobDueReasonBadgeColor,
  getJobDueReasonLabel,
  getJobDueReasonMetaText,
  getJobNatureLabel,
  getJobNatureBadgeColor,
} from './pmsJobHelpers'
import SearchableSelect from '../../../../shared/components/SearchableSelect'

const JOB_REQUEST_MODAL_STYLE: React.CSSProperties = {
  background: 'rgba(0,0,0,0.5)',
  position: 'fixed',
  inset: 0,
  zIndex: 1040,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}

type CompanyGroup = {id: number; name: string}
type Subcompany = {id: number; name: string; companyId: number}
type JobDisplayRecord = PmsJobRecord & {
  __source: 'job' | 'periodicity'
  __planLine?: PmsPlanLineDto
  __hasOpenJob?: boolean
}

type CombinedGroup = {
  key: string
  job: JobDisplayRecord
  history: PmsJobRecord[]
  planLine?: PmsPlanLineDto
  hasOpenJob?: boolean
  source: 'job' | 'periodicity'
}

type CreateUnplannedJobContext = {
  initialVesselId?: number
  initialEquipmentId?: number
  initialSubComponentId?: number
  initialPlanLineId?: number
  lockPlanLine?: boolean
}

const isRunningHoursSchedule = (scheduleType?: string | null) =>
  scheduleType === 'RUNNING_HOURS' ||
  scheduleType === 'HYBRID_WHICHEVER_FIRST' ||
  scheduleType === 'HYBRID_BOTH_REQUIRED'

const isTimeSchedule = (scheduleType?: string | null) =>
  scheduleType === 'TIME' ||
  scheduleType === 'HYBRID_WHICHEVER_FIRST' ||
  scheduleType === 'HYBRID_BOTH_REQUIRED'

const PmsJobListPage: FC = () => {
  const {currentUser, auth, roleDetails, hasPermission, isSuperAdmin, permissionsLoaded} = useAuth()
  const { data: accessibleVessels } = useAccessibleVessels()
  const roleId = Number((auth?.userDetails as any)?.roleId ?? currentUser?.role?.id ?? 0) || 0
  const effectiveBehavior = (
    roleDetails?.effectiveBehaviorType ||
    (currentUser?.role as any)?.baseRoleType ||
    currentUser?.role?.roleName ||
    ''
  ).toUpperCase()
  const isCrew = roleId === 4 || effectiveBehavior === 'CREW'
  const location = useLocation()
  const {state: globalState, vessels: globalVessels} = useGlobalFilter()
  const canReadJobs = hasPermission(TECH_PERM.READ_PMS_JOB)
  const canCreateJobs = hasPermission(TECH_PERM.CREATE_PMS_JOB)
  const canUpdateJobs = hasPermission(TECH_PERM.UPDATE_PMS_JOB)
  const canDeleteJobs = hasPermission(TECH_PERM.DELETE_PMS_JOB)
  const canCompleteJobs = hasPermission(TECH_PERM.COMPLETE_PMS_JOB)
  const canDownloadPms = hasPermission(TECH_PERM.DOWNLOAD_PMS)
  const canMaskJobs = isSuperAdmin || (permissionsLoaded && hasPermission(TECH_PERM.MASK_PMS_JOB))
  const canUseBatchActions = canUpdateJobs || canDeleteJobs
  const canShowEditPlanLineAction = canUpdateJobs
  const canShowDeletePlanLineAction = canDeleteJobs

  const parseQueryParams = () => {
    const params = new URLSearchParams(window.location.search)
    const stateParam = params.get('state')
    const fromParam = params.get('from')
    let initialState = ''
    if (fromParam === 'action-center-overdue') {
      initialState = 'OVERDUE'
    } else if (stateParam) {
      initialState = stateParam.toUpperCase()
      if (initialState === 'SCHEDULED_COMPLETED') {
        initialState = 'SCHEDULED_ALL'
      }
    }
    return {
      state: initialState,
      search: params.get('globalSearch') || '',
      openRequests: params.get('tab') === 'requests' || params.get('modal') === 'job-requests',
    }
  }

  const initialParams = parseQueryParams()

  const [filters, setFilters] = useState<{
    companyId: string
    subcompanyId: string
    vesselId: string
    state: string
    criticality: string
    scheduleType: string
    jobNature: string
    equipmentIds: number[]
    fromDate?: string
    toDate?: string
  }>({
    companyId: '',
    subcompanyId: '',
    vesselId: '',
    state: initialParams.state,
    criticality: '',
    scheduleType: '',
    jobNature: '',
    equipmentIds: [],
    fromDate: initialParams.state === 'COMPLETED' ? new Date(Date.now() - 30 * 86400000).toISOString().split('T')[0] : '',
    toDate: initialParams.state === 'COMPLETED' ? new Date().toISOString().split('T')[0] : '',
  })

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const stateParam = params.get('state')
    const fromParam = params.get('from')
    const tabParam = params.get('tab')
    const modalParam = params.get('modal')
    const globalSearchParam = params.get('globalSearch')

    if (fromParam === 'action-center-overdue') {
      setFilters((prev) => ({...prev, state: 'OVERDUE'}))
    } else if (stateParam !== null) {
      let targetState = stateParam ? stateParam.toUpperCase() : ''
      if (targetState === 'SCHEDULED_COMPLETED') {
        targetState = 'SCHEDULED_ALL'
      }
      setFilters((prev) => {
        const next = {...prev, state: targetState}
        if (targetState === 'COMPLETED') {
          if (!next.fromDate) {
            next.fromDate = new Date(Date.now() - 30 * 86400000).toISOString().split('T')[0]
          }
          if (!next.toDate) {
            next.toDate = new Date().toISOString().split('T')[0]
          }
        }
        return next
      })
    }

    if (globalSearchParam !== null) {
      setSearchTerm(globalSearchParam)
    }

    if (tabParam === 'requests' || modalParam === 'job-requests') {
      setJobRequestTab('PENDING')
      setJobRequestPage(1)
      setJobRequestModalOpen(true)
    }
  }, [location.search])

  const [combinedPage, setCombinedPage] = useState<PagedResult<PmsJobCombinedDto>>({
    content: [],
    totalPages: 0,
    totalElements: 0,
    number: 0,
    size: 10,
  })
  const [plan, setPlan] = useState<PmsPlanDto | null>(null)
  const [loading, setLoading] = useState(false)
  const [companies, setCompanies] = useState<CompanyGroup[]>([])
  const [subcompanies, setSubcompanies] = useState<Subcompany[]>([])
  const [vessels, setVessels] = useState<Vessel[]>([])
  const [vesselsScoped, setVesselsScoped] = useState<Vessel[]>([])

  const [viewOpen, setViewOpen] = useState(false)
  const [viewing, setViewing] = useState<PmsJobRecord | null>(null)
  const [completeOpen, setCompleteOpen] = useState(false)
  const [completing, setCompleting] = useState<PmsJobRecord | null>(null)
  const [deferOpen, setDeferOpen] = useState(false)
  const [deferring, setDeferring] = useState<PmsJobRecord | null>(null)
  const [creatingPeriodicityAction, setCreatingPeriodicityAction] = useState<string | null>(null)
  const [historyOpen, setHistoryOpen] = useState(false)
  const [historyJob, setHistoryJob] = useState<PmsJobRecord | null>(null)
  const [historyRecords, setHistoryRecords] = useState<PmsJobRecord[]>([])
  const [createJobOpen, setCreateJobOpen] = useState(false)
  const [createJobContext, setCreateJobContext] = useState<CreateUnplannedJobContext | null>(null)

  // Job Plan + Periodicities
  const [addPlanOpen, setAddPlanOpen] = useState(false)
  const [addLineOpen, setAddLineOpen] = useState(false)
  const [editLineOpen, setEditLineOpen] = useState(false)
  const [editingLine, setEditingLine] = useState<PmsPlanLineDto | null>(null)
  const [editingLineLocked, setEditingLineLocked] = useState(false)
  const [jobMaskingOpen, setJobMaskingOpen] = useState(false)

  // Pagination
  const [currentPage, setCurrentPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [searchTerm, setSearchTerm] = useState(initialParams.search)

  // Sync global header vessel filter → local filters
  useEffect(() => {
    const gvId = globalState.vessel?.id
    if (gvId != null) {
      setFilters((prev) => ({
        ...prev,
        vesselId: String(gvId),
      }))
    } else if (isCrew && currentUser?.vessel?.id) {
      setFilters((prev) => ({
        ...prev,
        vesselId: String(currentUser.vessel.id),
      }))
    } else {
      setFilters((prev) => ({
        ...prev,
        vesselId: '',
      }))
    }
  }, [globalState.vessel, isCrew, currentUser?.vessel?.id])

  // Sync global header company filter → local company/subcompany
  useEffect(() => {
    const gc = globalState.company
    if (gc) {
      if (gc.type === 'company_group') {
        setFilters((prev) => ({...prev, companyId: String(gc.id), subcompanyId: ''}))
      } else {
        // company_admin → set subcompany + find parent company
        const matched = subcompanies.find((s) => s.id === gc.id)
        setFilters((prev) => ({
          ...prev,
          companyId: matched?.companyId ? String(matched.companyId) : prev.companyId,
          subcompanyId: String(gc.id),
        }))
      }
    } else {
      // Company cleared — reset local
      setFilters((prev) => ({...prev, companyId: '', subcompanyId: ''}))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalState.company])

  useEffect(() => {
    setSearchTerm(globalState.searchQuery || '')
  }, [globalState.searchQuery])
  const [jobStats, setJobStats] = useState<{
    total: number
    activeTotal?: number
    overdue: number
    overdueUnplanned?: number
    scheduledCompleted?: number
    scheduled: number
    completed: number
  } | null>(null)
  const [loadingJobStats, setLoadingJobStats] = useState(false)
  const [loadingLookups, setLoadingLookups] = useState(false)
  const [exportingPms, setExportingPms] = useState(false)
  const tableLoading = loading || loadingLookups
  const filtersDisabled = tableLoading
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const todayDate = useMemo(() => new Date().toISOString().split('T')[0], [])
  const minCompletedDate = useMemo(() => {
    const d = new Date()
    d.setMonth(d.getMonth() - 3)
    return d.toISOString().split('T')[0]
  }, [])

  // Sorting
  const [sortColumn, setSortColumn] = useState<keyof PmsJobRecord | ''>('dueDate')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')

  // Batch selection
  const [selectedJobs, setSelectedJobs] = useState<Set<number>>(new Set())
  const [batchActionOpen, setBatchActionOpen] = useState(false)

  // Job Requests
  const [jobRequestModalOpen, setJobRequestModalOpen] = useState(false)
  const [jobRequestTab, setJobRequestTab] = useState<'PENDING' | 'ACCEPTED' | 'REJECTED'>('PENDING')
  const [jobRequestSearch, setJobRequestSearch] = useState('')
  const [jobRequestSearchDebounced, setJobRequestSearchDebounced] = useState('')
  const [jobRequestPage, setJobRequestPage] = useState(1)
  const [jobRequestPageSize, setJobRequestPageSize] = useState(10)
  const [jobRequestPageData, setJobRequestPageData] = useState<PagedResult<QHSEDefectDto>>({
    content: [],
    totalPages: 0,
    totalElements: 0,
    number: 0,
    size: 10,
  })
  const [jobRequestPendingCount, setJobRequestPendingCount] = useState(0)
  const [loadingJobRequests, setLoadingJobRequests] = useState(false)
  const [acceptModalOpen, setAcceptModalOpen] = useState(false)
  const [rejectModalOpen, setRejectModalOpen] = useState(false)
  const [selectedRequest, setSelectedRequest] = useState<QHSEDefectDto | null>(null)
  const [viewDefectOpen, setViewDefectOpen] = useState(false)
  const [viewDefectRecord, setViewDefectRecord] = useState<QHSEDefectDto | null>(null)
  const [loadingDefectId, setLoadingDefectId] = useState<number | null>(null)
  const [expandedSubcomponentRequests, setExpandedSubcomponentRequests] = useState<Set<number>>(
    new Set()
  )
  const [expandedJobRequests, setExpandedJobRequests] = useState<Set<number>>(new Set())
  const allowedVesselIds = useMemo(() => {
    if (!globalState.company) return null
    if (!globalVessels || globalVessels.length === 0) return null
    return new Set(globalVessels.map((v: any) => Number(v.id)))
  }, [globalState.company, globalVessels])

  const combinedGroups = useMemo<CombinedGroup[]>(() => {
    let content = combinedPage.content ?? []
    if (allowedVesselIds != null) {
      content = content.filter((item) => {
        const vId = Number(item.job?.vesselId ?? item.planLine?.vesselId)
        return !vId || allowedVesselIds.has(vId)
      })
    }
    return content.map((item, index) => {
      const record: JobDisplayRecord = {
        ...(item.job as PmsJobRecord),
        __source: item.source,
        __planLine: item.planLine ?? undefined,
        __hasOpenJob: item.hasOpenJob ?? false,
      }
      return {
        key: `${item.source}-${record.id ?? item.planLine?.id ?? index}`,
        job: record,
        history: [],
        planLine: item.planLine ?? undefined,
        hasOpenJob: item.hasOpenJob ?? false,
        source: item.source,
      }
    })
  }, [combinedPage.content, allowedVesselIds])

  const filterMaskedSearchResults = (
    page: PagedResult<PmsJobCombinedDto>,
    query?: string
  ): PagedResult<PmsJobCombinedDto> => {
    if (!query?.trim()) return page
    const content = (page.content ?? []).filter((item) => !Boolean(item.job?.masked))
    if (content.length === (page.content ?? []).length) return page
    return {
      ...page,
      content,
    }
  }

  const toggleExpandedSubcomponents = (id: number) => {
    setExpandedSubcomponentRequests((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  const toggleExpandedJobs = (id: number) => {
    setExpandedJobRequests((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  useEffect(() => {
    loadLookups()
  }, [accessibleVessels, roleDetails])

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setDebouncedSearch(searchTerm.trim())
    }, 300)
    return () => window.clearTimeout(handle)
  }, [searchTerm])

  const combinedRequestId = useRef(0)
  const planRequestId = useRef(0)
  const statsRequestId = useRef(0)

  useEffect(() => {
    loadCombinedPage()
  }, [filters, debouncedSearch, currentPage, rowsPerPage, sortColumn, sortOrder])

  useEffect(() => {
    const vesselId = filters.vesselId ? Number(filters.vesselId) : undefined
    loadPlanForVessel(vesselId).then((plan) => loadJobStats(vesselId, plan?.id ?? null))
  }, [filters.vesselId])

  const loadLookups = async () => {
    setLoadingLookups(true)
    try {
      const [groupsRaw, adminsRaw, vesselsData] = await Promise.all([
        getCompanyAdminList?.().catch(() => []),
        getCompanyList?.().catch(() => []),
        getVesselList().catch(() => [] as Vessel[]),
      ])

      const groups: CompanyGroup[] = Array.isArray(groupsRaw)
        ? groupsRaw.map((g: any) => ({
            id: Number(g.id),
            name: g.name ?? g.companyGroupAdminName ?? `Group #${g.id}`,
          }))
        : []

      const admins: Subcompany[] = Array.isArray(adminsRaw)
        ? adminsRaw.map((a: any) => ({
            id: Number(a.id),
            name: a.name ?? a.companyAdminName ?? `Company #${a.id}`,
            companyId: Number(
              a.cgaid?.id ?? a.cga?.id ?? a.companyGroupAdminId ?? a.companyGroupId
            ),
          }))
        : []

      setCompanies(groups.filter((g) => Number.isFinite(g.id)))
      setSubcompanies(admins.filter((a) => Number.isFinite(a.id) && Number.isFinite(a.companyId)))

      // Scope vessels using authoritative helper (supports custom roles and assigned vessels)
      const vesselsForUser = getAccessibleVessels(
        vesselsData,
        currentUser,
        isSuperAdmin,
        roleDetails,
        accessibleVessels
      )

      setVessels(vesselsData)
      setVesselsScoped(vesselsForUser)

      if (isCrew && currentUser?.vessel?.id) {
        setFilters((prev) => ({...prev, vesselId: String(currentUser.vessel.id)}))
      } else if (globalState.vessel?.id) {
        setFilters((prev) => ({...prev, vesselId: String(globalState.vessel.id)}))
      }
    } catch (error) {
      console.error('Error loading lookups:', error)
    } finally {
      setLoadingLookups(false)
    }
  }

  const loadCombinedPage = async (options?: {vesselId?: number; page?: number}) => {
    const requestId = ++combinedRequestId.current
    setLoading(true)
    try {
      const vesselId =
        options?.vesselId ?? (filters.vesselId ? Number(filters.vesselId) : undefined)
      const pageToLoad = options?.page ?? currentPage
      const isDueByDefectFilter = filters.state === 'DUE_BY_DEFECT'
      const isCompletedFilter = filters.state === 'COMPLETED'
      const combinedStateFilter = isDueByDefectFilter ? 'OVERDUE' : filters.state || undefined
      const combinedScheduleTypeFilter = isDueByDefectFilter ? 'DEFECT' : undefined
      const response = await searchPmsJobsCombined({
        vesselId,
        state: combinedStateFilter,
        fromDate: isCompletedFilter ? (filters.fromDate || undefined) : undefined,
        toDate: isCompletedFilter ? (filters.toDate || undefined) : undefined,
        criticality: filters.criticality || undefined,
        scheduleType: combinedScheduleTypeFilter,
        equipmentIds: filters.equipmentIds,
        jobNature: filters.jobNature || undefined,
        q: debouncedSearch || undefined,
        page: Math.max(pageToLoad - 1, 0),
        size: rowsPerPage,
        sortBy: sortColumn || (isCompletedFilter ? 'actualCompletionDate' : 'dueDate'),
        sortDir: sortOrder,
      })
      if (requestId !== combinedRequestId.current) return
      if (filters.vesselId && Number(filters.vesselId) !== vesselId) return
      setCombinedPage({
        ...response,
        content: filterMaskedSearchResults(response, debouncedSearch).content,
      })
      if (typeof response?.number === 'number') {
        setCurrentPage(response.number + 1)
      }
    } catch (error) {
      if (requestId === combinedRequestId.current) {
        console.error('Error loading jobs:', error)
        toast.error('Failed to load jobs', {position: 'top-center'})
      }
    } finally {
      if (requestId === combinedRequestId.current) {
        setLoading(false)
      }
    }
  }

  const loadData = async (options?: {vesselId?: number; page?: number}) => {
    const vesselId =
      options?.vesselId ?? (filters.vesselId ? Number(filters.vesselId) : undefined)
    await Promise.all([
      loadCombinedPage(options),
      loadJobStats(vesselId),
      loadJobRequestPendingCount(),
    ])
  }

  const loadPlanForVessel = async (vesselId?: number): Promise<PmsPlanDto | null> => {
    if (!vesselId) {
      setPlan(null)
      return null
    }
    const requestId = ++planRequestId.current
    const requestedVesselId = vesselId
    try {
      const plans = await getPmsPlans(vesselId)
      const activePlan = plans.find((p) => p.status === 'ACTIVE') ?? plans[0] ?? null
      if (requestId !== planRequestId.current) return null
      if (filters.vesselId && Number(filters.vesselId) !== requestedVesselId) return null
      setPlan(activePlan ?? null)
      return activePlan ?? null
    } catch (error) {
      if (requestId === planRequestId.current) {
        console.error('Error loading job plan/periodicities:', error)
        toast.error('Failed to load job plan periodicities', {position: 'top-center'})
        setPlan(null)
      }
      return null
    }
  }

  const loadJobStats = async (vesselId?: number, planId?: number | null) => {
    if (!vesselId) {
      setJobStats(null)
      return
    }
    const requestId = ++statsRequestId.current
    const requestedVesselId = vesselId
    setLoadingJobStats(true)
    try {
      const stats = await getPmsJobSummaryStats(vesselId)
      if (requestId !== statsRequestId.current) return
      if (filters.vesselId && Number(filters.vesselId) !== requestedVesselId) return
      setJobStats(stats)
    } catch (error) {
      if (requestId === statsRequestId.current) {
        console.error('Error loading job stats:', error)
        setJobStats(null)
      }
    } finally {
      if (requestId === statsRequestId.current) {
        setLoadingJobStats(false)
      }
    }
  }

  const loadJobRequestPendingCount = async () => {
    try {
      const vesselId = filters.vesselId ? Number(filters.vesselId) : undefined
      if (!vesselId) {
        setJobRequestPendingCount(0)
        return
      }
      const page = await getJobRequestsPage({
        vesselId,
        status: ['PENDING'],
        page: 0,
        size: 1,
      })
      setJobRequestPendingCount(page.totalElements ?? 0)
    } catch (error) {
      console.error('Error loading job request pending count:', error)
      setJobRequestPendingCount(0)
    }
  }

  const loadJobRequestPage = async () => {
    if (!jobRequestModalOpen) return
    setLoadingJobRequests(true)
    try {
      const vesselId = filters.vesselId ? Number(filters.vesselId) : undefined
      if (!vesselId) {
        setJobRequestPageData({
          content: [],
          totalPages: 0,
          totalElements: 0,
          number: 0,
          size: jobRequestPageSize,
        })
        setLoadingJobRequests(false)
        return
      }
      const page = await getJobRequestsPage({
        vesselId,
        status: [jobRequestTab],
        search: jobRequestSearchDebounced,
        page: Math.max(jobRequestPage - 1, 0),
        size: jobRequestPageSize,
      })
      setJobRequestPageData(page)
      if (typeof page?.number === 'number') {
        setJobRequestPage(page.number + 1)
      }
    } catch (error) {
      console.error('Error loading job requests:', error)
      toast.error('Failed to load job requests', {position: 'top-center'})
    } finally {
      setLoadingJobRequests(false)
    }
  }

  useEffect(() => {
    loadJobRequestPendingCount()
  }, [filters.vesselId])

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setJobRequestSearchDebounced(jobRequestSearch.trim())
    }, 300)
    return () => window.clearTimeout(handle)
  }, [jobRequestSearch])

  useEffect(() => {
    loadJobRequestPage()
  }, [
    jobRequestModalOpen,
    jobRequestTab,
    jobRequestPage,
    jobRequestPageSize,
    jobRequestSearchDebounced,
    filters.vesselId,
  ])

  const handleDeletePlanLine = async (lineId: number) => {
    if (!window.confirm('Are you sure you want to delete this periodicity (plan line)?')) {
      return
    }
    try {
      await deletePmsPlanLine(lineId)
      toast.success('Periodicity deleted successfully', {position: 'top-center'})
      await loadData()
    } catch (error: any) {
      console.error('Error deleting periodicity (plan line):', error)
      toast.error('Failed to delete periodicity (plan line)', {
        position: 'top-center',
      })
    }
  }

  const handleCreatePlan = async (data: PmsPlanDto): Promise<PmsPlanDto> => {
    try {
      const created = await createPmsPlan(data)
      toast.success('Job plan created successfully', {position: 'top-center'})
      setAddPlanOpen(false)
      setPlan(created)
      if (created.vesselId && String(created.vesselId) !== filters.vesselId) {
        setFilters((prev) => ({...prev, vesselId: String(created.vesselId)}))
      }
      return created
    } catch (error: any) {
      console.error('Error creating job plan:', error)
      toast.error('Failed to create job plan', {position: 'top-center'})
      throw error
    }
  }

  const handleAcceptRequest = (request: QHSEDefectDto) => {
    setSelectedRequest(request)
    setAcceptModalOpen(true)
  }

  const handleRejectRequest = (request: QHSEDefectDto) => {
    setSelectedRequest(request)
    setRejectModalOpen(true)
  }

  const handleAcceptConfirm = async (remarks?: string) => {
    if (!selectedRequest?.id) return
    try {
      await acceptJobRequest(selectedRequest.id, remarks)
      const jobCount =
        selectedRequest.jobPlanLineIds && selectedRequest.jobPlanLineIds.length > 0
          ? selectedRequest.jobPlanLineIds.length
          : selectedRequest.jobPlanLineId
          ? 1
          : 0
      toast.success(`Job request accepted and ${jobCount || 1} job(s) created successfully`, {
        position: 'top-center',
      })
      setAcceptModalOpen(false)
      setSelectedRequest(null)
      await loadJobRequestPendingCount()
      await loadJobRequestPage()
      await loadData()
    } catch (error: any) {
      console.error('Error accepting job request:', error)
      toast.error('Failed to accept job request', {
        position: 'top-center',
      })
    }
  }

  const handleRejectConfirm = async (reason: string) => {
    if (!selectedRequest?.id) return
    try {
      await rejectJobRequest(selectedRequest.id, reason)
      toast.success('Job request rejected', {position: 'top-center'})
      setRejectModalOpen(false)
      setSelectedRequest(null)
      await loadJobRequestPendingCount()
      await loadJobRequestPage()
    } catch (error: any) {
      console.error('Error rejecting job request:', error)
      toast.error('Failed to reject job request', {
        position: 'top-center',
      })
    }
  }

  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const {name, value} = e.target
    setCurrentPage(1)
    setFilters((prev) => {
      const next = {...prev, [name]: value}
      if (name === 'companyId') {
        next.subcompanyId = ''
        next.vesselId = ''
      }
      if (name === 'subcompanyId') {
        next.vesselId = ''
      }
      if (name === 'state' && value === 'COMPLETED') {
        if (!next.fromDate) {
          next.fromDate = new Date(Date.now() - 30 * 86400000).toISOString().split('T')[0]
        }
        if (!next.toDate) {
          next.toDate = todayDate
        }
      }
      if (name === 'fromDate' && value) {
        if (value < minCompletedDate) {
          next.fromDate = minCompletedDate
        }
        if (next.toDate && next.fromDate > next.toDate) {
          next.toDate = next.fromDate
        }
      }
      if (name === 'toDate' && value) {
        if (value > todayDate) {
          next.toDate = todayDate
        }
        if (next.fromDate && next.toDate < next.fromDate) {
          next.fromDate = next.toDate
        }
      }
      return next
    })
  }

  const handleClearFilters = () => {
    setCurrentPage(1)
    setFilters((prev) => ({
      ...prev,
      state: '',
      criticality: '',
      scheduleType: '',
      jobNature: '',
      equipmentIds: [],
      fromDate: '',
      toDate: '',
    }))
    setSearchTerm('')
  }

  const isOverdue = (record: PmsJobRecord): boolean => {
    if (record.state !== 'OVERDUE' && record.state !== 'IN_PROGRESS') return false
    if (!record.dueDate) return false
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const dueDate = new Date(record.dueDate)
    return dueDate < today
  }

  const getOverdueDays = (record: PmsJobRecord): number => {
    if (!isOverdue(record) || !record.dueDate) return 0
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const dueDate = new Date(record.dueDate)
    dueDate.setHours(0, 0, 0, 0)
    return Math.floor((today.getTime() - dueDate.getTime()) / (1000 * 60 * 60 * 24))
  }

  const isPeriodicityOverdue = (line?: PmsPlanLineDto | null): boolean => {
    if (!line || line.active === false) return false
    const today = new Date()
    today.setHours(0, 0, 0, 0)

    const hasTime = isTimeSchedule(line.scheduleType ?? undefined)
    const hasCounter = isRunningHoursSchedule(line.scheduleType ?? undefined)

    let timeDue = false
    let counterDue = false

    if (hasTime && line.nextDueDate) {
      const dueDate = new Date(line.nextDueDate)
      dueDate.setHours(0, 0, 0, 0)
      timeDue = dueDate <= today
    }

    if (hasCounter && line.nextDueCounter != null && line.runningHourCounterValue != null) {
      counterDue = line.runningHourCounterValue >= line.nextDueCounter
    }

    if (line.scheduleType === 'HYBRID_BOTH_REQUIRED') {
      return timeDue && counterDue
    }
    if (line.scheduleType === 'HYBRID_WHICHEVER_FIRST') {
      return timeDue || counterDue
    }

    if (line.scheduleType === 'TIME') return timeDue
    if (line.scheduleType === 'RUNNING_HOURS') return counterDue
    return false
  }

  /**
   * Returns true when today (or current running hours) falls within the last 20%
   * of the plan line's periodicity window — i.e. the job is "coming due soon".
   *
   * Periodicity conversion (matches backend PmsJobStateService):
   *   M → jobPeriodicity × 30 days
   *   W → jobPeriodicity × 7  days
   *   D → jobPeriodicity       days
   *   H → jobPeriodicity / 24  days  (time-based)
   *   H → jobPeriodicity       hours (running-hours-based)
   */
  const isWithinEarlyWindow = (line?: PmsPlanLineDto | null): boolean => {
    if (!line || line.active === false) return false

    const EARLY_PERCENT = 20
    const hasTime = isTimeSchedule(line.scheduleType ?? undefined)
    const hasCounter = isRunningHoursSchedule(line.scheduleType ?? undefined)

    let timeInWindow = false
    let counterInWindow = false

    // --- Time-based: check if today >= (dueDate - 20% of interval) ---
    if (hasTime && line.nextDueDate && line.jobPeriodicity && line.periodicityId) {
      const pid = line.periodicityId.trim().toUpperCase()
      let intervalDays = 0
      if (pid === 'M') intervalDays = line.jobPeriodicity * 30
      else if (pid === 'W') intervalDays = line.jobPeriodicity * 7
      else if (pid === 'D') intervalDays = line.jobPeriodicity
      else if (pid === 'H') intervalDays = Math.floor(line.jobPeriodicity / 24)

      if (intervalDays > 0) {
        const windowDays = Math.ceil(intervalDays * EARLY_PERCENT / 100)
        const today = new Date()
        today.setHours(0, 0, 0, 0)
        const dueDate = new Date(line.nextDueDate)
        dueDate.setHours(0, 0, 0, 0)
        const windowStart = new Date(dueDate)
        windowStart.setDate(dueDate.getDate() - windowDays)
        // In window when today >= windowStart (but not yet overdue — overdue is handled separately)
        timeInWindow = today >= windowStart
      }
    }

    // --- Running-hours-based: check if currentCounter >= (dueCounter - 20% of periodicity hours) ---
    if (
      hasCounter &&
      line.nextDueCounter != null &&
      line.runningHourCounterValue != null &&
      line.jobPeriodicity != null
    ) {
      // For RUNNING_HOURS the periodicity is already in hours
      const windowHours = line.jobPeriodicity * EARLY_PERCENT / 100
      const threshold = line.nextDueCounter - windowHours
      counterInWindow = line.runningHourCounterValue >= threshold
    }

    if (line.scheduleType === 'HYBRID_BOTH_REQUIRED') return timeInWindow && counterInWindow
    if (line.scheduleType === 'HYBRID_WHICHEVER_FIRST') return timeInWindow || counterInWindow
    if (line.scheduleType === 'TIME') return timeInWindow
    if (line.scheduleType === 'RUNNING_HOURS') return counterInWindow
    return false
  }

  const isGroupOverdue = (group: CombinedGroup): boolean => {
    const job = group.job
    if (job.__source === 'periodicity') {
      return isPeriodicityOverdue(group.planLine ?? job.__planLine ?? null)
    }
    if (job.state === 'OVERDUE') return true
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    if (job.dueDate) {
      const dueDate = new Date(job.dueDate)
      dueDate.setHours(0, 0, 0, 0)
      return dueDate < today
    }
    if (!job.dueDate && job.dueCounter) {
      return job.state === 'IN_PROGRESS'
    }
    return false
  }

  const getDisplayedRowState = (
    record: PmsJobRecord & {__source?: 'job' | 'periodicity'; __planLine?: PmsPlanLineDto},
    source?: 'job' | 'periodicity',
    hasOpenJob?: boolean | null,
    planLine?: PmsPlanLineDto | null
  ): string => {
    const isPeriodicity = (source ?? record.__source) === 'periodicity'
    if (isPeriodicity && !hasOpenJob) {
      return isPeriodicityOverdue(planLine ?? record.__planLine ?? null) ? 'OVERDUE' : 'SCHEDULED'
    }
    return getDisplayedJobState(record)
  }

  const getDisplayedRowStateBadgeColor = (
    record: PmsJobRecord & {__source?: 'job' | 'periodicity'; __planLine?: PmsPlanLineDto},
    source?: 'job' | 'periodicity',
    hasOpenJob?: boolean | null,
    planLine?: PmsPlanLineDto | null
  ): string => {
    const displayedState = getDisplayedRowState(record, source, hasOpenJob, planLine)
    if (displayedState === 'OVERDUE') return 'badge-light-danger'
    if (displayedState === 'SCHEDULED') return 'badge-light-success'
    return getDisplayedJobStateBadgeColor(record)
  }

  const openLinkedDefect = async (defectId?: number | null) => {
    if (!defectId) return
    setLoadingDefectId(defectId)
    try {
      const defect = await getDefect(defectId)
      setViewDefectRecord(defect)
      setViewDefectOpen(true)
    } catch (error) {
      console.error('Failed to load defect details:', error)
      toast.error('Unable to load defect details', {position: 'top-center'})
    } finally {
      setLoadingDefectId(null)
    }
  }

  const filteredGroups = useMemo(() => {
    if (!filters.jobNature) return combinedGroups
    return combinedGroups.filter((g) => {
      const job = g.job
      const nature = job.jobNature ?? (job.isBreakdown ? 'UNPLANNED' : (job.planLineId || g.planLine ? 'RECURRING' : 'SINGLE_TIME'))
      if (filters.jobNature === 'SINGLE_TIME') return nature === 'SINGLE_TIME'
      if (filters.jobNature === 'RECURRING') return nature === 'RECURRING'
      if (filters.jobNature === 'UNPLANNED') return nature === 'UNPLANNED' || Boolean(job.isBreakdown)
      return true
    })
  }, [combinedGroups, filters.jobNature])

  const handleSort = (column: keyof PmsJobRecord) => {
    setCurrentPage(1)
    setSortColumn((prevCol) => {
      if (prevCol === column) {
        setSortOrder((prevDir) => (prevDir === 'asc' ? 'desc' : 'asc'))
        return prevCol
      }
      setSortOrder('desc')
      return column
    })
  }

  const isSelectableJob = (group: CombinedGroup) =>
    group.job.__source === 'job' && typeof group.job.id === 'number' && group.job.id > 0

  const handleSelectJob = (jobId: number) => {
    setSelectedJobs((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(jobId)) {
        newSet.delete(jobId)
      } else {
        newSet.add(jobId)
      }
      return newSet
    })
  }

  const handleSelectAll = () => {
    const selectableIds = currentGroups
      .filter((group) => isSelectableJob(group as CombinedGroup))
      .map((group) => group.job.id!)
    if (selectedJobs.size === selectableIds.length) {
      setSelectedJobs(new Set())
    } else {
      setSelectedJobs(new Set(selectableIds))
    }
  }

  const subcompaniesForChosenCompany = useMemo(() => {
    if (!filters.companyId) return []
    const cid = Number(filters.companyId)
    return subcompanies.filter((sc) => sc.companyId === cid)
  }, [filters.companyId, subcompanies])

  const filteredVessels = useMemo(() => {
    let list = vesselsScoped
    if (filters.companyId) {
      const cid = Number(filters.companyId)
      list = list.filter((v: any) => Number(v.companyGroupAdmin?.id ?? v.companyGroupId) === cid)
    }
    if (filters.subcompanyId) {
      const scid = Number(filters.subcompanyId)
      list = list.filter((v: any) => Number(v.companyAdmin?.id ?? v.companyId) === scid)
    }
    return list
  }, [vesselsScoped, filters.companyId, filters.subcompanyId])

  const totalElements = combinedPage.totalElements ?? 0
  const pageSize = combinedPage.size ?? rowsPerPage
  const indexOfFirstRecord = totalElements > 0 ? (currentPage - 1) * pageSize : 0
  const currentGroups = filteredGroups
  const indexOfLastRecord = Math.min(indexOfFirstRecord + currentGroups.length, totalElements)
  const totalPages = combinedPage.totalPages ?? 0
  const selectableCount = currentGroups.filter((group) =>
    isSelectableJob(group as CombinedGroup)
  ).length
  const showVesselColumn = useMemo(() => {
    if (isCrew) return false
    if (!filters.vesselId) return true
    const vesselsInRows = new Set(
      currentGroups
        .map((group) => group.job.vesselId ?? group.job.vesselName ?? null)
        .filter((v) => v !== null && v !== undefined)
    )
    return vesselsInRows.size > 1
  }, [isCrew, filters.vesselId, currentGroups])
  const tableColSpan = showVesselColumn ? 11 : 10
  const pmsJobsTableColSpan = tableColSpan + 1

  const jobRequestItems = jobRequestPageData.content ?? []
  const jobRequestTotalElements = jobRequestPageData.totalElements ?? 0
  const jobRequestTotalPages = Math.max(jobRequestPageData.totalPages ?? 1, 1)
  const jobRequestStart =
    jobRequestTotalElements === 0 ? 0 : (jobRequestPage - 1) * jobRequestPageSize + 1
  const jobRequestEnd =
    jobRequestTotalElements === 0
      ? 0
      : Math.min(jobRequestStart + jobRequestItems.length - 1, jobRequestTotalElements)

  const handlePageChange = (page: number) => {
    if (page > 0 && page <= totalPages) setCurrentPage(page)
  }

  const handleRowsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setRowsPerPage(parseInt(e.target.value, 10))
    setCurrentPage(1)
  }

  const handleView = async (record: PmsJobRecord) => {
    try {
      const displayRecord = record.masked && record.id ? await getPmsJobDisplay(record.id) : record
      setViewing(displayRecord)
      setViewOpen(true)
    } catch (error: any) {
      console.error('Error loading job details:', error)
      toast.error('Failed to load job details', {position: 'top-center'})
    }
  }

  const showMaskedJobRestriction = () => {
    toast.error('For this job to update, please contact your admin.', {position: 'top-center'})
  }

  const handleComplete = (record: PmsJobRecord) => {
    setCompleting(record)
    setCompleteOpen(true)
  }

  const handleOpenDefer = (record: PmsJobRecord) => {
    setDeferring(record)
    setDeferOpen(true)
  }

  const createOverdueJobForPeriodicityAction = async (
    group: CombinedGroup,
    action: 'complete' | 'defer'
  ) => {
    const planLine = group.planLine ?? group.job.__planLine ?? null
    if (!planLine?.id) {
      toast.error('Job plan is required before this action can be performed.', {
        position: 'top-center',
      })
      return
    }

    const actionKey = `${action}-${planLine.id}`
    setCreatingPeriodicityAction(actionKey)
    try {
      const createdJob = (await forceOverduePlanLineJob(planLine.id, {
        jobDueReason: 'OTHER',
        remarks: 'Created from overdue periodicity action',
      })) as PmsJobRecord

      if (action === 'complete') {
        handleComplete(createdJob)
      } else {
        handleOpenDefer(createdJob)
      }
      loadData()
    } catch (error: any) {
      console.error('Error creating overdue PMS job:', error)
      toast.error(error?.message || 'Failed to create overdue job', {position: 'top-center'})
      loadData()
    } finally {
      setCreatingPeriodicityAction(null)
    }
  }

  const openCreateUnplannedJob = (group: CombinedGroup) => {
    const planLine = group.planLine ?? group.job.__planLine ?? null
    setCreateJobContext({
      initialVesselId: group.job.vesselId || undefined,
      initialEquipmentId: group.job.equipmentId ?? planLine?.equipmentId ?? undefined,
      initialSubComponentId: group.job.subComponentId ?? planLine?.subComponentId ?? undefined,
      initialPlanLineId: planLine?.id ?? undefined,
      lockPlanLine: Boolean(planLine?.id),
    })
    setCreateJobOpen(true)
  }

  const handleOpenHistory = (group: CombinedGroup) => {
    setHistoryJob(group.job)
    setHistoryRecords(group.history)
    setHistoryOpen(true)
  }

  function getJobTypeBadgeColor(jobType?: string | null): string {
    if (!jobType) return 'badge-light'
    switch (jobType.toUpperCase()) {
      case 'I':
        return 'badge-light-info' // Inspect
      case 'G':
        return 'badge-light-primary' // General
      case 'O':
        return 'badge-light-warning' // Overhaul
      case 'C':
        return 'badge-light-secondary' // Conditional
      case 'A':
        return 'badge-light-success' // Adjust
      case 'R':
        return 'badge-light-danger' // Renew
      case 'S':
        return 'badge-light-info' // Service
      case 'T':
        return 'badge-light-primary' // Test
      case 'M':
        return 'badge-light-warning' // Maintenance
      case 'D':
        return 'badge-light-secondary' // Dismantle
      case 'L':
        return 'badge-light-success' // Lubricate
      default:
        return 'badge-light'
    }
  }

  function getJobTypeLabel(jobType?: string | null): string {
    if (!jobType) return '-'
    switch (jobType.toUpperCase()) {
      case 'I':
        return 'Inspect'
      case 'G':
        return 'General'
      case 'O':
        return 'Overhaul'
      case 'C':
        return 'Conditional'
      case 'A':
        return 'Adjust'
      case 'R':
        return 'Renew'
      case 'S':
        return 'Service'
      case 'T':
        return 'Test'
      case 'M':
        return 'Maintenance'
      case 'D':
        return 'Dismantle'
      case 'L':
        return 'Lubricate'
      default:
        return jobType
    }
  }

  const isOpenState = (state: PmsJobState) => ['SCHEDULED', 'OVERDUE', 'IN_PROGRESS', 'DEFERRED'].includes(state)

  const formatInterval = (record: PmsJobRecord): string => {
    const unitMap: Record<string, string> = {
      M: 'Months',
      W: 'Weeks',
      D: 'Days',
      H: 'Hours',
    }
    const build = (value?: number | null, unit?: string | null) => {
      if (!value || !unit) return ''
      const label = unitMap[unit.toUpperCase()] ?? unit
      return `${value} ${label}`
    }
    const primary = build(record.jobPeriodicity ?? null, record.periodicityId ?? null)
    const secondary = build(record.jobPeriodicity2 ?? null, record.periodicityId2 ?? null)
    if (primary && secondary) return `${primary} / ${secondary}`
    return primary || secondary || '-'
  }

  const formatNextDue = (record: PmsJobRecord): string => {
    const parts: string[] = []
    if (record.dueDate) parts.push(formatJobDate(record.dueDate))
    if (record.dueCounter != null) parts.push(`${record.dueCounter.toLocaleString()} hrs`)
    return parts.length ? parts.join(' / ') : '-'
  }

  const formatCurrentRunningHours = (planLine?: PmsPlanLineDto | null): string => {
    if (!planLine || !isRunningHoursSchedule(planLine.scheduleType)) return '-'
    return planLine.runningHourCounterValue != null
      ? `${planLine.runningHourCounterValue.toLocaleString()} hrs`
      : '-'
  }

  const handleExport = async () => {
    if (!canDownloadPms) {
      toast.error('You do not have permission to export PMS data.', {position: 'top-center'})
      return
    }
    if (!filters.vesselId) {
      toast.info('Select a vessel before exporting PMS jobs.', {position: 'top-center'})
      return
    }
    if (exportingPms) return

    const vesselId = Number(filters.vesselId)
    const exportPageSize = 200
    const isDueByDefectFilter = filters.state === 'DUE_BY_DEFECT'
    const isCompletedFilter = filters.state === 'COMPLETED'
    const combinedStateFilter = isDueByDefectFilter ? 'OVERDUE' : filters.state || undefined
    const combinedScheduleTypeFilter = isDueByDefectFilter ? 'DEFECT' : undefined

    setExportingPms(true)
    try {
      const loadPage = async (page: number) =>
        searchPmsJobsCombined({
          vesselId,
          state: combinedStateFilter,
          fromDate: isCompletedFilter ? (filters.fromDate || undefined) : undefined,
          toDate: isCompletedFilter ? (filters.toDate || undefined) : undefined,
          criticality: filters.criticality || undefined,
          scheduleType: combinedScheduleTypeFilter,
          equipmentIds: filters.equipmentIds,
          jobNature: filters.jobNature || undefined,
          q: debouncedSearch || undefined,
          page,
          size: exportPageSize,
          sortBy: sortColumn || (isCompletedFilter ? 'actualCompletionDate' : 'dueDate'),
          sortDir: sortOrder,
        })

      const firstPage = await loadPage(0)
      const allItems: PmsJobCombinedDto[] = [
        ...(filterMaskedSearchResults(firstPage, debouncedSearch).content ?? []),
      ]
      for (let page = 1; page < (firstPage.totalPages ?? 0); page += 1) {
        const nextPage = await loadPage(page)
        allItems.push(...(filterMaskedSearchResults(nextPage, debouncedSearch).content ?? []))
      }

      if (allItems.length === 0) {
        toast.info('No PMS jobs found for the selected filters.', {position: 'top-center'})
        return
      }

      const rows = allItems.map((item, index) => {
        const job = item.job
        const planLine = item.planLine ?? null
        const hasOpenJob = item.hasOpenJob ?? false
        const rowState = getDisplayedRowState(job, item.source, hasOpenJob, planLine)

        const rowData: Record<string, any> = {
          'Sr No': index + 1,
          Source: item.source,
        }
        if (!isCrew) {
          rowData['Vessel'] = job.vesselName || ''
        }
        return {
          ...rowData,
          'Job Classification': getJobNatureLabel(job),
          Equipment: job.equipmentName || planLine?.equipmentName || '',
          Subcomponent: job.subComponentName || planLine?.subComponentName || '',
          'Job Code': job.jobCode || planLine?.jobCode || '',
          'Job Description': job.jobDescription || job.title || planLine?.taskDescription || '',
          Interval: formatInterval(job),
          'Next Due': formatNextDue(job),
          'Current Running Hours': formatCurrentRunningHours(planLine),
          'Job Due Reason': getJobDueReasonLabel(job),
          'Due Reason Details': getJobDueReasonMetaText(job),
          State: rowState,
          Criticality: job.criticality || planLine?.criticality || '',
          'Last Job Done': job.lastDoneDate
            ? formatJobDate(job.lastDoneDate)
            : job.lastDoneCounter != null
            ? `${job.lastDoneCounter.toLocaleString()} hrs`
            : '',
          'Plan Line Id': planLine?.id ?? '',
          'Job Id': job.id ?? '',
        }
      })

      const workbook = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(rows), 'PMS Jobs')
      XLSX.writeFile(workbook, `pms-jobs-${new Date().toISOString().slice(0, 10)}.xlsx`)
    } catch (error: any) {
      console.error('Error exporting PMS jobs:', error)
      toast.error('Failed to export PMS jobs', {position: 'top-center'})
    } finally {
      setExportingPms(false)
    }
  }

  return (
    <div className='app-main flex-column flex-row-fluid' id='kt_app_main'>
      <div className='d-flex flex-column flex-column-fluid'>
        <div id='kt_app_content' className='app-content flex-column-fluid bg-white'>
          <div className='card'>
            <div className='card-header border-0 pt-6 d-flex justify-content-between bg-white'>
              <div>
                <h3 className='card-label text-dark fw-bold'>PMS Jobs</h3>
                <span className='text-muted fs-7'>
                  Manage periodicities (plan lines) and job execution in one place
                </span>
              </div>
              <div className='card-toolbar d-flex gap-2'>
                {selectedJobs.size > 0 && canUseBatchActions && (
                  <>
                    <button
                      type='button'
                      className='btn btn-sm btn-warning'
                      onClick={() => {
                        if (selectedJobs.size === 0) {
                          toast.error('Please select at least one job', {position: 'top-center'})
                          return
                        }
                        setBatchActionOpen(true)
                      }}
                    >
                      <KTSVG
                        path='/media/icons/duotune/general/gen044.svg'
                        className='svg-icon-2'
                      />
                      Batch Actions ({selectedJobs.size})
                    </button>
                    <button
                      type='button'
                      className='btn btn-sm btn-light'
                      onClick={() => setSelectedJobs(new Set())}
                    >
                      Clear Selection
                    </button>
                  </>
                )}
                {filters.vesselId && (
                  <>
                    {canMaskJobs && (
                      <button
                        type='button'
                        className='btn btn-sm btn-danger shadow-sm'
                        onClick={() => setJobMaskingOpen(true)}
                      >
                        <KTSVG
                          path='/media/icons/duotune/general/gen043.svg'
                          className='svg-icon-2 me-1'
                        />
                        Job Masking
                      </button>
                    )}
                    {canReadJobs && (
                      <button
                        type='button'
                        className='btn btn-sm btn-light-warning position-relative btn-outline btn-outline-warning'
                        onClick={() => {
                          setJobRequestTab('PENDING')
                          setJobRequestPage(1)
                          setJobRequestModalOpen(true)
                        }}
                      >
                        <KTSVG
                          path='/media/icons/duotune/general/gen044.svg'
                          className='svg-icon-2 me-1'
                        />
                        Job Requests
                        {jobRequestPendingCount > 0 && (
                          <span
                            className='badge bg-danger text-white position-absolute top-0 start-100 translate-middle'
                            style={{fontSize: '10px'}}
                          >
                            {jobRequestPendingCount}
                          </span>
                        )}
                      </button>
                    )}
                    {canDownloadPms && (
                      <button
                        type='button'
                        className='btn btn-sm btn-light-success btn-outline'
                        onClick={handleExport}
                        disabled={exportingPms}
                      >
                        <KTSVG
                          path='/media/icons/duotune/general/download.svg'
                          className='svg-icon-2 me-1'
                        />
                        {exportingPms ? 'Exporting...' : 'Export'}
                      </button>
                    )}
                  </>
                )}
                {canCreateJobs && (
                  <button
                    type='button'
                    className='btn btn-sm btn-primary'
                    onClick={() => {
                      if (filters.vesselId) {
                        setCreateJobContext({initialVesselId: Number(filters.vesselId)})
                      }
                      setCreateJobOpen(true)
                    }}
                  >
                    <KTSVG
                      path='/media/icons/duotune/arrows/arr075.svg'
                      className='svg-icon-2 me-1'
                    />
                    Create Job
                  </button>
                )}
              </div>
            </div>

            {/* Filters */}
            <div className='card-body py-4 bg-white border-top'>
              <div className='row gx-3 gy-3 mb-4'>
                {/* Status */}
                <div className='col-md-2'>
                  <label className='form-label fw-semibold fs-7' style={{color: '#A1A5B7'}}>
                    Status
                  </label>
                  <SearchableSelect
                    className='form-select form-select-sm'
                    name='state'
                    value={filters.state}
                    onChange={handleFilterChange}
                    disabled={filtersDisabled}
                  >
                    <option value=''>All</option>
                    <option value='OVERDUE'>Overdue</option>
                    <option value='DUE_BY_DEFECT'>Due by Defect</option>
                    <option value='SCHEDULED_20_PERCENT'>Due in 20% Periodicity</option>
                    <option value='SCHEDULED_ALL'>All Scheduled</option>
                    <option value='DEFERRED'>Deferred</option>
                    <option value='COMPLETED'>Completed</option>
                  </SearchableSelect>
                </div>

                {filters.state === 'COMPLETED' && (
                  <>
                    <div className='col-md-2'>
                      <label className='form-label fw-semibold fs-7' style={{color: '#A1A5B7'}}>
                        Completed From
                      </label>
                      <input
                        type='date'
                        className='form-control form-control-sm'
                        name='fromDate'
                        value={filters.fromDate || ''}
                        min={minCompletedDate}
                        max={filters.toDate || todayDate}
                        onChange={handleFilterChange}
                        disabled={filtersDisabled}
                      />
                    </div>
                    <div className='col-md-2'>
                      <div className='d-flex justify-content-between align-items-center mb-1'>
                        <label className='form-label fw-semibold fs-7 mb-0' style={{color: '#A1A5B7'}}>
                          Completed To
                        </label>
                        {filters.fromDate !== minCompletedDate ? (
                          <button
                            type='button'
                            className='btn btn-link btn-color-primary p-0 fs-8 text-decoration-underline'
                            onClick={() => {
                              setCurrentPage(1)
                              setFilters((prev) => ({
                                ...prev,
                                fromDate: minCompletedDate,
                                toDate: todayDate,
                              }))
                            }}
                            title='Filter by the maximum allowable 3 months range'
                          >
                            Last 3 Months
                          </button>
                        ) : (
                          <button
                            type='button'
                            className='btn btn-link btn-color-primary p-0 fs-8 text-decoration-underline'
                            onClick={() => {
                              setCurrentPage(1)
                              setFilters((prev) => ({
                                ...prev,
                                fromDate: new Date(Date.now() - 30 * 86400000).toISOString().split('T')[0],
                                toDate: todayDate,
                              }))
                            }}
                            title='Filter by the last 30 days'
                          >
                            Last 30 Days
                          </button>
                        )}
                      </div>
                      <input
                        type='date'
                        className='form-control form-control-sm'
                        name='toDate'
                        value={filters.toDate || ''}
                        min={filters.fromDate || minCompletedDate}
                        max={todayDate}
                        onChange={handleFilterChange}
                        disabled={filtersDisabled}
                      />
                    </div>
                  </>
                )}

                {/* Criticality Filter */}
                <div className='col-md-2'>
                  <label className='form-label fw-semibold fs-7' style={{color: '#A1A5B7'}}>
                    Criticality
                  </label>
                  <SearchableSelect
                    className='form-select form-select-sm'
                    name='criticality'
                    value={filters.criticality}
                    onChange={handleFilterChange}
                    disabled={filtersDisabled}
                  >
                    <option value=''>All</option>
                    <option value='CRITICAL'>Critical</option>
                    <option value='HIGH'>High</option>
                    <option value='MEDIUM'>Medium</option>
                    <option value='LOW'>Low</option>
                  </SearchableSelect>
                </div>

                {/* Job Type Filter */}
                <div className='col-md-2'>
                  <label className='form-label fw-semibold fs-7' style={{color: '#A1A5B7'}}>
                    Job Type
                  </label>
                  <SearchableSelect
                    className='form-select form-select-sm'
                    name='jobNature'
                    value={filters.jobNature}
                    onChange={handleFilterChange}
                    disabled={filtersDisabled}
                  >
                    <option value=''>All Job Types</option>
                    <option value='SINGLE_TIME'>Single-Time (One-Off)</option>
                    <option value='RECURRING'>Recurring (Periodicity)</option>
                    <option value='UNPLANNED'>Unplanned / Defect</option>
                  </SearchableSelect>
                </div>

                {/* Schedule Type Filter
                <div className='col-md-2'>
                  <label className='form-label fw-semibold fs-7' style={{ color: '#A1A5B7' }}>
                    Schedule Type
                  </label>
                  <SearchableSelect
                    className='form-select form-select-sm'
                    name='scheduleType'
                    value={filters.scheduleType}
                    onChange={handleFilterChange}
                    disabled={filtersDisabled}
                  >
                    <option value=''>All</option>
                    <option value='DEFECT'>Defect (Due to Defect)</option>
                    <option value='TIME'>Time-based</option>
                    <option value='RUNNING_HOURS'>Running Hours</option>
                    <option value='HYBRID_WHICHEVER_FIRST'>Hybrid (Whichever First)</option>
                    <option value='HYBRID_BOTH_REQUIRED'>Hybrid (Both Required)</option>
                    <option value='EVENT'>Event-based</option>
                  </SearchableSelect>
                </div>
                */}

                {/* Search */}
                <div className='col-md-3'>
                  <label className='form-label fw-semibold fs-7' style={{color: '#A1A5B7'}}>
                    Search Jobs
                  </label>
                  <div className='position-relative'>
                    <div
                      className='position-absolute ms-3'
                      style={{top: '50%', transform: 'translateY(-50%)', zIndex: 1, pointerEvents: 'none'}}
                    >
                      <KTSVG
                        path='/media/icons/duotune/general/gen021.svg'
                        className='svg-icon-2'
                      />
                    </div>
                    <input
                      type='text'
                      className='form-control form-control-sm ps-10 pe-8'
                      placeholder='Search job code, title, equipment...'
                      value={searchTerm}
                      onChange={(e) => {
                        setSearchTerm(e.target.value)
                        setCurrentPage(1)
                      }}
                    />
                    {Boolean(searchTerm) && (
                      <button
                        type='button'
                        className='btn btn-flush position-absolute end-0 top-50 translate-middle-y me-2 p-0 border-0 bg-transparent text-muted'
                        style={{cursor: 'pointer', lineHeight: 1}}
                        onClick={() => {
                          setSearchTerm('')
                          setDebouncedSearch('')
                          setCurrentPage(1)
                        }}
                        title='Clear search'
                      >
                        <span className='fs-4 fw-bold text-gray-500'>×</span>
                      </button>
                    )}
                  </div>
                </div>

                <div className='col-md-2 d-flex align-items-end'>
                  <button
                    type='button'
                    className='btn btn-sm btn-light'
                    onClick={handleClearFilters}
                    disabled={filtersDisabled}
                  >
                    Clear All
                  </button>
                </div>
              </div>

              {filters.vesselId ? (
                <div className='mb-3 text-muted fs-7'>
                  {loadingJobStats ? (
                    'Loading job summary...'
                  ) : jobStats ? (
                    (() => {
                      const activeTotal =
                        jobStats.activeTotal ?? (jobStats.overdue + jobStats.scheduled)
                      const isCompletedSelected = filters.state === 'COMPLETED'
                      const isOverdueSelected = filters.state === 'OVERDUE'
                      const isScheduledSelected =
                        filters.state === 'SCHEDULED_ALL' ||
                        filters.state === 'SCHEDULED_COMPLETED' ||
                        filters.state === 'SCHEDULED_20_PERCENT'
                      const isAllSelected = !filters.state
                      const hasSearch = Boolean(debouncedSearch.trim())
                      const hasDateFilter = Boolean(filters.fromDate || filters.toDate)
                      const totalDisplayed = combinedPage.totalElements ?? 0

                      return (
                        <div className='d-flex flex-wrap align-items-center gap-1'>
                          <span>Jobs:</span>
                          <strong className='text-dark'>{activeTotal} Active</strong>
                          {isAllSelected && (hasSearch || totalDisplayed !== activeTotal) && (
                            <span className='text-primary fw-semibold'>
                              ({totalDisplayed} displayed)
                            </span>
                          )}
                          <span className='mx-1'>|</span>

                          <span>Overdue:</span>
                          <strong className={jobStats.overdue > 0 ? 'text-danger' : 'text-dark'}>
                            {jobStats.overdue}
                          </strong>
                          {isOverdueSelected &&
                            (hasSearch || totalDisplayed !== jobStats.overdue) && (
                              <span className='text-primary fw-semibold'>
                                ({totalDisplayed} displayed)
                              </span>
                            )}
                          <span className='mx-1'>|</span>

                          <span>Scheduled:</span>
                          <strong className='text-dark'>{jobStats.scheduled}</strong>
                          {isScheduledSelected &&
                            (hasSearch || totalDisplayed !== jobStats.scheduled) && (
                              <span className='text-primary fw-semibold'>
                                ({totalDisplayed} displayed)
                              </span>
                            )}
                          <span className='mx-1'>|</span>

                          <span>Completed:</span>
                          <strong className='text-dark'>{jobStats.completed} Total</strong>
                          {isCompletedSelected ? (
                            hasDateFilter ? (
                              <span className='text-primary fw-semibold'>
                                ({totalDisplayed} in selected dates)
                              </span>
                            ) : (
                              (hasSearch || totalDisplayed !== jobStats.completed) && (
                                <span className='text-primary fw-semibold'>
                                  ({totalDisplayed} displayed)
                                </span>
                              )
                            )
                          ) : null}
                        </div>
                      )
                    })()
                  ) : (
                    'Job summary unavailable'
                  )}
                </div>
              ) : (
                <div className='mb-3 text-muted fs-7'>
                  <span style={{fontWeight: 600, color: '#1D4ED8'}}>
                    {combinedPage.totalElements ?? 0}
                  </span>{' '}
                  <span style={{color: '#3B82F6'}}>Results across all vessels</span>{' '}
                  <span style={{fontSize: '12px', color: '#94A3B8'}}>
                    Select a specific vessel above to see detailed job stats
                  </span>
                </div>
              )}

              {/* Table */}
              <div className='report-table table-responsive' style={{position: 'relative'}}>
                {tableLoading && (
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      background: 'rgba(255,255,255,0.6)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      zIndex: 5,
                    }}
                  >
                    <div className='spinner-border' role='status'>
                      <span className='visually-hidden'>Loading...</span>
                    </div>
                  </div>
                )}

                <div style={{overflowX: 'auto'}}>
                  <table className='table table-bordered align-middle'>
                    <thead className='table-header text-start'>
                      <tr>
                        <th style={{minWidth: '50px'}} className='text-center'>
                          <input
                            type='checkbox'
                            className='form-check-input'
                            checked={selectableCount > 0 && selectedJobs.size === selectableCount}
                            onChange={handleSelectAll}
                            disabled={selectableCount === 0}
                            title='Select All'
                          />
                        </th>
                        <th style={{minWidth: '70px'}} className='text-center text-nowrap'>
                          Sr/No
                        </th>
                        <th style={{minWidth: '300px'}}>
                          <div className='d-flex align-items-center text-nowrap'>
                            <span className='me-1'>Equipment &gt; Subcomponent</span>
                          </div>
                        </th>
                        {showVesselColumn && (
                          <th style={{minWidth: '180px'}}>
                            <div className='d-flex align-items-center text-nowrap'>
                              <span className='me-1'>Vessel</span>
                              <button
                                type='button'
                                className='btn btn-link p-0 m-0 pb-1'
                                onClick={() => handleSort('vesselName')}
                                disabled={combinedGroups.length === 0}
                              >
                                <KTSVG
                                  path={`/media/map/sort-col-${
                                    sortColumn === 'vesselName'
                                      ? sortOrder === 'asc'
                                        ? 'up-black'
                                        : 'down-black'
                                      : 'grey'
                                  }.svg`}
                                  className='svg-icon-3'
                                />
                              </button>
                            </div>
                          </th>
                        )}
                        {/* <th style={{ minWidth: '140px' }}>
                          <div className='d-flex align-items-center text-nowrap'>
                            <span className='me-1'>Job Code</span>
                            <button
                              type='button'
                              className='btn btn-link p-0 m-0 pb-1'
                              onClick={() => handleSort('jobCode')}
                              disabled={combinedGroups.length === 0}
                            >
                              <KTSVG
                                path={`/media/map/sort-col-${
                                  sortColumn === 'jobCode'
                                    ? sortOrder === 'asc'
                                      ? 'up-black'
                                      : 'down-black'
                                    : 'grey'
                                }.svg`}
                                className='svg-icon-3'
                              />
                            </button>
                          </div>
                        </th> */}
                        {/* <th style={{ minWidth: '100px' }}>
                          <div className='d-flex align-items-center text-nowrap'>
                            <span className='me-1'>Job Type</span>
                          </div>
                        </th> */}
                        <th style={{minWidth: '250px'}}>
                          <div className='d-flex align-items-center text-nowrap'>
                            <span className='me-1'>Job Description</span>
                            <button
                              type='button'
                              className='btn btn-link p-0 m-0 pb-1'
                              onClick={() => handleSort('title')}
                              disabled={combinedGroups.length === 0}
                            >
                              <KTSVG
                                path={`/media/map/sort-col-${
                                  sortColumn === 'title'
                                    ? sortOrder === 'asc'
                                      ? 'up-black'
                                      : 'down-black'
                                    : 'grey'
                                }.svg`}
                                className='svg-icon-3'
                              />
                            </button>
                          </div>
                        </th>
                        {/* <th style={{ minWidth: '250px' }}>
                          <div className='d-flex align-items-center text-nowrap'>
                            <span className='me-1'>Job Instructions</span>
                          </div>
                        </th> */}
                        {/* <th style={{ minWidth: '160px' }}>
                          <div className='d-flex align-items-center text-nowrap'>
                            <span className='me-1'>Schedule Type</span>
                          </div>
                        </th> */}
                        <th style={{minWidth: '160px'}}>
                          <div className='d-flex align-items-center text-nowrap'>
                            <span className='me-1'>Interval</span>
                          </div>
                        </th>
                        <th style={{minWidth: '180px'}}>
                          <div className='d-flex align-items-center text-nowrap'>
                            <span className='me-1'>
                              {filters.state === 'COMPLETED' ? 'Completed Date' : 'Next Due'}
                            </span>
                          </div>
                        </th>
                        <th style={{minWidth: '170px'}}>
                          <div className='d-flex align-items-center text-nowrap'>
                            <span className='me-1'>
                              {filters.state === 'COMPLETED' ? 'Completed Hours' : 'Current Running Hours'}
                            </span>
                          </div>
                        </th>
                        {/* <th style={{ minWidth: '160px' }}>
                          <div className='d-flex align-items-center text-nowrap'>
                            <span className='me-1'>Due Date / Counter</span>
                            <button
                              type='button'
                              className='btn btn-link p-0 m-0 pb-1'
                              onClick={() => handleSort('dueDate')}
                              disabled={combinedGroups.length === 0}
                            >
                              <KTSVG
                                path={`/media/map/sort-col-${
                                  sortColumn === 'dueDate'
                                    ? sortOrder === 'asc'
                                      ? 'up-black'
                                      : 'down-black'
                                    : 'grey'
                                }.svg`}
                                className='svg-icon-3'
                              />
                            </button>
                          </div>
                        </th> */}
                        <th style={{minWidth: '140px'}}>
                          <div className='d-flex align-items-center text-nowrap'>
                            <span className='me-1'>Job Due Reason</span>
                          </div>
                        </th>
                        <th style={{minWidth: '130px'}}>
                          <div className='d-flex align-items-center text-nowrap'>
                            <span className='me-1'>Last Job Done</span>
                            <button
                              type='button'
                              className='btn btn-link p-0 m-0 pb-1'
                              onClick={() => handleSort('lastDoneDate')}
                              disabled={combinedGroups.length === 0}
                            >
                              <KTSVG
                                path={`/media/map/sort-col-${
                                  sortColumn === 'lastDoneDate'
                                    ? sortOrder === 'asc'
                                      ? 'up-black'
                                      : 'down-black'
                                    : 'grey'
                                }.svg`}
                                className='svg-icon-3'
                              />
                            </button>
                          </div>
                        </th>
                        <th style={{minWidth: '120px'}}>
                          <div className='d-flex align-items-center text-nowrap'>
                            <span className='me-1'>State</span>
                            <button
                              type='button'
                              className='btn btn-link p-0 m-0 pb-1'
                              onClick={() => handleSort('state')}
                              disabled={combinedGroups.length === 0}
                            >
                              <KTSVG
                                path={`/media/map/sort-col-${
                                  sortColumn === 'state'
                                    ? sortOrder === 'asc'
                                      ? 'up-black'
                                      : 'down-black'
                                    : 'grey'
                                }.svg`}
                                className='svg-icon-3'
                              />
                            </button>
                          </div>
                        </th>
                        <th style={{minWidth: '200px', textAlign: 'center'}}>Actions</th>
                      </tr>
                    </thead>
                    <tbody
                      className='table-body text-start'
                      style={{
                        opacity: tableLoading && currentGroups.length > 0 ? 0.55 : 1,
                        transition: 'opacity 0.15s ease-in-out',
                        pointerEvents: tableLoading ? 'none' : 'auto',
                      }}
                    >
                      {tableLoading && currentGroups.length === 0 ? (
                        <tr>
                          <td colSpan={pmsJobsTableColSpan} className='text-center py-5'>
                            <div className='spinner-border text-primary' role='status'>
                              <span className='visually-hidden'>Loading...</span>
                            </div>
                          </td>
                        </tr>
                      ) : currentGroups.length === 0 ? (
                        <tr>
                          <td colSpan={pmsJobsTableColSpan} className='text-center py-5 text-muted'>
                            {debouncedSearch
                              ? `No periodicities or jobs matching "${debouncedSearch}"`
                              : 'No periodicities or jobs found'}
                          </td>
                        </tr>
                      ) : (
                        currentGroups.map((group, idx) => {
                          const record = group.job
                          const isPeriodicityRow = record.__source === 'periodicity'
                          const hasOpenJob = group.hasOpenJob ?? record.__hasOpenJob ?? false
                          const periodicityOverdue =
                            isPeriodicityRow && !hasOpenJob
                              ? isPeriodicityOverdue(group.planLine ?? record.__planLine ?? null)
                              : false
                          const displayedRowState = getDisplayedRowState(
                            record,
                            record.__source,
                            hasOpenJob,
                            group.planLine ?? record.__planLine ?? null
                          )
                          const displayedRowStateBadge = getDisplayedRowStateBadgeColor(
                            record,
                            record.__source,
                            hasOpenJob,
                            group.planLine ?? record.__planLine ?? null
                          )
                          const isCompletedRow =
                            record.state === 'COMPLETED' ||
                            record.state === 'CLOSED' ||
                            record.state === 'VERIFIED' ||
                            filters.state === 'COMPLETED'
                          // Build hierarchy with badges similar to ViewPlanModal
                          const hierarchyElements = []
                          const displayEquipmentName =
                            record.equipmentName || (group.planLine ?? record.__planLine)?.equipmentName
                          if (displayEquipmentName) {
                            hierarchyElements.push(
                              <span key='eq' className='badge badge-light-primary me-1'>
                                <KTSVG
                                  path='/media/icons/duotune/general/gen025.svg'
                                  className='svg-icon-3 me-1'
                                />
                                {displayEquipmentName}
                              </span>
                            )
                          }
                          const displaySubComponentName =
                            record.subComponentName || (group.planLine ?? record.__planLine)?.subComponentName
                          if (displaySubComponentName) {
                            hierarchyElements.push(
                              <span key='sub' className='badge badge-light-success me-1 fs-6'>
                                <KTSVG
                                  path='/media/icons/duotune/general/gen023.svg'
                                  className='svg-icon-3 me-1'
                                />
                                {displaySubComponentName}
                              </span>
                            )
                          }
                          const isUnplannedJob = Boolean(record.isBreakdown)
                          const canSelect = isSelectableJob(group as CombinedGroup)
                          const showHistory = group.history.length > 0
                          const planLineForActions = group.planLine ?? record.__planLine
                          const isMaskedPreviousJob =
                            Boolean(record.masked) &&
                            !isPeriodicityRow &&
                            Boolean(planLineForActions)
                          const rowJobDueReasonLabel = isMaskedPreviousJob
                            ? getPlannedJobReasonLabel(record)
                            : getJobDueReasonLabel(record)
                          const rowJobDueReasonMeta = isMaskedPreviousJob
                            ? getPlannedTriggerDescription(record)
                            : getJobDueReasonMetaText(record)
                          const isOverdueItem = periodicityOverdue || record.state === 'OVERDUE'
                          const rowHierarchyBadgeLabel = isCompletedRow
                            ? 'Completed'
                            : isMaskedPreviousJob
                            ? 'Scheduled'
                            : isOverdueItem
                            ? 'Overdue'
                            : isPeriodicityRow
                            ? 'Scheduled'
                            : isUnplannedJob
                            ? (record.state === 'SCHEDULED' ? 'Scheduled' : 'Overdue')
                            : 'Planned'

                          return (
                            <tr key={record.id ?? group.key}>
                              <td className='text-center'>
                                <input
                                  type='checkbox'
                                  className='form-check-input'
                                  checked={canSelect && selectedJobs.has(record.id!)}
                                  onChange={() => {
                                    if (canSelect) handleSelectJob(record.id!)
                                  }}
                                  title={
                                    canSelect ? 'Select Job' : 'Periodicities are not selectable'
                                  }
                                  disabled={!canSelect}
                                />
                              </td>
                              <td className='text-center'>{indexOfFirstRecord + idx + 1}</td>
                              <td>
                                <div className='d-flex flex-wrap align-items-center gap-1'>
                                  {hierarchyElements.length > 0 ? (
                                    hierarchyElements
                                  ) : (
                                    <span className='text-muted'>-</span>
                                  )}
                                  <span
                                    className={`badge ${
                                      isCompletedRow
                                        ? 'badge-light-primary'
                                        : isMaskedPreviousJob
                                        ? 'badge-light-success'
                                        : isOverdueItem
                                        ? 'badge-light-danger'
                                        : isPeriodicityRow
                                        ? 'badge-light-success'
                                        : isUnplannedJob && record.state !== 'SCHEDULED'
                                        ? 'badge-light-danger'
                                        : 'badge-light-success'
                                    } fs-7`}
                                  >
                                    {rowHierarchyBadgeLabel}
                                  </span>
                                  <span className={`badge ${getJobNatureBadgeColor(record)} fs-7 ms-1`}>
                                    {getJobNatureLabel(record)}
                                  </span>
                                </div>
                              </td>
                              {showVesselColumn && <td>{record.vesselName || '-'}</td>}
                              {/* <td className='fw-semibold'>
                                {record.jobCode && showHistory ? (
                                  <button
                                    type='button'
                                    className='btn btn-link p-0 m-0 align-baseline fw-semibold'
                                    onClick={() => handleOpenHistory(group)}
                                  >
                                    {record.jobCode}
                                  </button>
                                ) : record.jobCode ? (
                                  record.jobCode
                                ) : showHistory ? (
                                  <button
                                    type='button'
                                    className='btn btn-link p-0 m-0 align-baseline fw-semibold'
                                    onClick={() => handleOpenHistory(group)}
                                  >
                                    History
                                  </button>
                                ) : (
                                  '-'
                                )}
                              </td> */}
                              {/* <td>
                                {record.jobType ? (
                                  <span className={`badge ${getJobTypeBadgeColor(record.jobType)}`} title={getJobTypeLabel(record.jobType)}>
                                    {record.jobType}
                                  </span>
                                ) : (
                                  '-'
                                )}
                              </td> */}
                              <td>
                                <span
                                  title={record.jobDescription || record.title}
                                  style={{
                                    maxWidth: '250px',
                                    display: 'inline-block',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                  }}
                                >
                                  {record.jobDescription || record.title || '-'}
                                </span>
                              </td>
                              {/* <td>
                                <span
                                  title={record.jobInstructions || undefined}
                                  style={{
                                    maxWidth: '250px',
                                    display: 'inline-block',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                  }}
                                >
                                  {record.jobInstructions || '-'}
                                </span>
                              </td> */}
                              {/* <td>{scheduleTypeLabel(record.scheduleType)}</td> */}
                              <td>{formatInterval(record)}</td>
                              <td>
                                {isCompletedRow ? (
                                  <div>
                                    <div className='fw-bold text-gray-800'>
                                      {record.actualCompletionDate
                                        ? formatJobDate(record.actualCompletionDate)
                                        : record.lastDoneDate
                                        ? formatJobDate(record.lastDoneDate)
                                        : '-'}
                                    </div>
                                    <div className='text-muted fs-8 mt-1'>
                                      Next cycle: {formatNextDue(record)}
                                    </div>
                                  </div>
                                ) : (
                                  formatNextDue(record)
                                )}
                              </td>
                              <td className='text-nowrap'>
                                {isCompletedRow ? (
                                  <div>
                                    <div className='fw-bold text-gray-800'>
                                      {record.actualCounterAtCompletion != null
                                        ? `${record.actualCounterAtCompletion.toLocaleString()} hrs`
                                        : record.lastDoneCounter != null
                                        ? `${record.lastDoneCounter.toLocaleString()} hrs`
                                        : '-'}
                                    </div>
                                    <div className='text-muted fs-8'>
                                      Current: {formatCurrentRunningHours(group.planLine ?? record.__planLine)}
                                    </div>
                                  </div>
                                ) : (
                                  formatCurrentRunningHours(group.planLine ?? record.__planLine)
                                )}
                              </td>
                              {/* <td className='text-nowrap'>
                                {record.scheduleType === 'HYBRID_WHICHEVER_FIRST' || record.scheduleType === 'HYBRID_BOTH_REQUIRED' ? (
                                  record.dueDate && record.dueCounter ? (
                                    (() => {
                                      const trigger = determineHybridTrigger(record)
                                      const dateStyle = trigger === 'counter' ? { opacity: 0.5 } : {}
                                      const counterStyle = trigger === 'date' ? { opacity: 0.5 } : {}
                                      return (
                                        <div>
                                          <span style={dateStyle}>{formatJobDate(record.dueDate)}</span> <span className="text-muted">OR</span> <span style={counterStyle}>{record.dueCounter.toLocaleString()} hrs</span>
                                          {isOverdue(record) && (
                                            <span className='badge badge-light-danger ms-2' title={`${getOverdueDays(record)} days overdue`}>
                                              {getOverdueDays(record)}d overdue
                                            </span>
                                          )}
                                        </div>
                                      )
                                    })()
                                  ) : record.dueDate ? (
                                    <div>
                                      {formatJobDate(record.dueDate)}
                                      {isOverdue(record) && (
                                        <span className='badge badge-light-danger ms-2' title={`${getOverdueDays(record)} days overdue`}>
                                          {getOverdueDays(record)}d overdue
                                        </span>
                                      )}
                                    </div>
                                  ) : record.dueCounter ? (
                                    <div>
                                      {record.dueCounter.toLocaleString()} hrs
                                    </div>
                                  ) : '-'
                                ) : record.dueDate ? (
                                  <div>
                                    {formatJobDate(record.dueDate)}
                                    {isOverdue(record) && (
                                      <span className='badge badge-light-danger ms-2' title={`${getOverdueDays(record)} days overdue`}>
                                        {getOverdueDays(record)}d overdue
                                      </span>
                                    )}
                                  </div>
                                ) : record.dueCounter ? (
                                  <div>
                                    {record.dueCounter.toLocaleString()} hrs
                                  </div>
                                ) : (
                                  '-'
                                )}
                              </td> */}
                              <td className='text-nowrap'>
                                <div className='d-flex flex-column gap-1'>
                                  <span
                                    className={`badge ${
                                      isCompletedRow
                                        ? 'badge-light-primary'
                                        : isMaskedPreviousJob
                                        ? 'badge-light-success'
                                        : getJobDueReasonBadgeColor(record)
                                    }`}
                                  >
                                    {isCompletedRow ? 'Completed' : rowJobDueReasonLabel}
                                  </span>
                                  <small className='text-muted fs-7'>
                                    {isCompletedRow && (record.responsibleRank || record.assignedToUserName)
                                      ? `By: ${record.responsibleRank || record.assignedToUserName}`
                                      : rowJobDueReasonMeta}
                                  </small>
                                  {record.defectId && (
                                    <button
                                      className='btn btn-sm btn-light-primary'
                                      onClick={() => openLinkedDefect(record.defectId)}
                                      disabled={loadingDefectId === record.defectId}
                                      style={{minWidth: '140px'}}
                                    >
                                      {loadingDefectId === record.defectId
                                        ? 'Loading...'
                                        : record.defectNumber
                                        ? `View defect ${record.defectNumber}`
                                        : 'View defect'}
                                    </button>
                                  )}
                                </div>
                              </td>
                              <td className='text-nowrap'>
                                {record.actualCompletionDate
                                  ? `${formatJobDate(record.actualCompletionDate)}${record.actualCounterAtCompletion != null ? ` (${record.actualCounterAtCompletion.toLocaleString()} hrs)` : ''}`
                                  : record.lastDoneDate
                                  ? formatJobDate(record.lastDoneDate)
                                  : record.lastDoneCounter != null
                                  ? `${record.lastDoneCounter.toLocaleString()} hrs`
                                  : '-'}
                              </td>
                              <td>
                                <div className='d-flex flex-wrap align-items-center gap-2'>
                                  <span className={`badge ${displayedRowStateBadge}`}>
                                    {displayedRowState}
                                  </span>
                                  {record.masked && isSuperAdmin && (
                                    <span className='badge badge-light-info'>Masked</span>
                                  )}
                                </div>
                              </td>
                              <td style={{textAlign: 'center'}}>
                                <div className='d-flex justify-content-center gap-1'>
                                  {canReadJobs && (
                                    <button
                                      className='btn btn-icon btn-sm'
                                      title='View Job'
                                      onClick={() => handleView(record)}
                                    >
                                      <KTSVG
                                        path='/media/map/ph_eye.svg'
                                        className='svg-icon-3 text-primary'
                                      />
                                    </button>
                                  )}
                                  {isMaskedPreviousJob && canCreateJobs ? (
                                    <button
                                      className='btn btn-sm btn-light'
                                      title='Create Unplanned Job'
                                      onClick={() => openCreateUnplannedJob(group)}
                                    >
                                      <KTSVG
                                        path='/media/icons/duotune/status/anchor.svg'
                                        className='svg-icon-3 text-warning'
                                      />
                                    </button>
                                  ) : isPeriodicityRow &&
                                    !hasOpenJob &&
                                    !periodicityOverdue &&
                                    canCreateJobs ? (
                                    <button
                                      className='btn btn-sm btn-light'
                                      title='Create Unplanned Job'
                                      onClick={() => openCreateUnplannedJob(group)}
                                    >
                                      <KTSVG
                                        path='/media/icons/duotune/status/anchor.svg'
                                        className='svg-icon-3 text-warning'
                                      />
                                    </button>
                                  ) : null}
                                  {!isPeriodicityRow &&
                                    isOpenState(record.state) &&
                                    !isMaskedPreviousJob && (
                                      <>
                                        {canCompleteJobs && (
                                          <button
                                            className='btn btn-sm btn-success'
                                            onClick={() =>
                                              record.masked
                                                ? showMaskedJobRestriction()
                                                : handleComplete(record)
                                            }
                                            title='Close Job'
                                          >
                                            Close
                                          </button>
                                        )}
                                        {canUpdateJobs && (
                                          <button
                                            className='btn btn-sm btn-warning'
                                            onClick={() =>
                                              record.masked
                                                ? showMaskedJobRestriction()
                                                : handleOpenDefer(record)
                                            }
                                            title='Defer Job'
                                          >
                                            Defer
                                          </button>
                                        )}
                                      </>
                                    )}
                                  {isPeriodicityRow &&
                                    !hasOpenJob &&
                                    (periodicityOverdue || isWithinEarlyWindow(planLineForActions)) &&
                                    planLineForActions?.id && (
                                      <>
                                        {canCompleteJobs && (
                                          <button
                                            className='btn btn-sm btn-success'
                                            onClick={() => handleComplete(record)}
                                            title='Close Job'
                                          >
                                            Close
                                          </button>
                                        )}
                                        {canUpdateJobs && (
                                          <button
                                            className='btn btn-sm btn-warning'
                                            onClick={() => handleOpenDefer(record)}
                                            title='Defer Job'
                                          >
                                            Defer
                                          </button>
                                        )}
                                      </>
                                    )}
                                  {!isCompletedRow &&
                                    planLineForActions &&
                                    (canShowEditPlanLineAction || canShowDeletePlanLineAction) && (
                                      <>
                                        {canShowEditPlanLineAction && (
                                          <button
                                            className='btn btn-sm btn-light'
                                            disabled={isMaskedPreviousJob}
                                            title='Edit Periodicity (Plan Line)'
                                            onClick={() => {
                                              if (isMaskedPreviousJob) {
                                                showMaskedJobRestriction()
                                                return
                                              }
                                              if (planLineForActions) {
                                                setEditingLine(planLineForActions)
                                                setEditingLineLocked(Boolean(record.masked))
                                                setEditLineOpen(true)
                                              }
                                            }}
                                          >
                                            <KTSVG
                                              path='/media/icons/duotune/general/gen055.svg'
                                              className='svg-icon-3'
                                            />
                                          </button>
                                        )}
                                        {canShowDeletePlanLineAction && (
                                          <button
                                            className='btn btn-sm btn-light'
                                            title='Delete Periodicity (Plan Line)'
                                            onClick={() => {
                                              if (isMaskedPreviousJob) {
                                                showMaskedJobRestriction()
                                                return
                                              }
                                              const lineId = planLineForActions?.id
                                              if (lineId) handleDeletePlanLine(lineId)
                                            }}
                                          >
                                            <KTSVG
                                              path='/media/icons/duotune/general/gen027.svg'
                                              className='svg-icon-3 text-danger'
                                            />
                                          </button>
                                        )}
                                      </>
                                    )}
                                </div>
                              </td>
                            </tr>
                          )
                        })
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {!loading && totalElements > 0 && (
                  <div
                    className='pagination-wrapper d-flex justify-content-between align-items-center py-3 border-top'
                    style={{
                      position: 'static',
                      bottom: 0,
                      backgroundColor: '#fff',
                      zIndex: 10,
                    }}
                  >
                    <div className='d-flex align-items-center'>
                      <span className='text-muted me-2'>Rows per page</span>
                      <SearchableSelect
                        className='form-select'
                        style={{
                          borderRadius: '20px',
                          width: '70px',
                          border: '1px solid #dee2e6',
                          fontSize: '14px',
                          padding: '4px 8px',
                        }}
                        value={rowsPerPage}
                        onChange={handleRowsPerPageChange}
                      >
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                      </SearchableSelect>
                    </div>

                    <div className='d-flex align-items-center'>
                      <span className='text-muted me-3' style={{fontSize: '14px'}}>
                        Showing{' '}
                        <strong>
                          {indexOfFirstRecord + 1}-{indexOfLastRecord}
                        </strong>{' '}
                        of <strong>{totalElements}</strong>
                      </span>

                      <nav>
                        <ul className='pagination pagination-sm mb-0' style={{gap: '2px'}}>
                          <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                            <button
                              className='page-link text-muted'
                              style={{
                                backgroundColor: '#f8f9fa',
                                border: '1px solid #dee2e6',
                                padding: '8px 12px',
                                fontSize: '14px',
                                borderRadius: '6px',
                              }}
                              onClick={() => handlePageChange(currentPage - 1)}
                              disabled={currentPage === 1}
                            >
                              ‹
                            </button>
                          </li>

                          {(() => {
                            const pages = []
                            const showPages = 5
                            let startPage = Math.max(1, currentPage - 2)
                            let endPage = Math.min(totalPages, startPage + showPages - 1)

                            if (endPage - startPage < showPages - 1) {
                              startPage = Math.max(1, endPage - showPages + 1)
                            }

                            for (let i = startPage; i <= endPage; i++) {
                              pages.push(
                                <li
                                  key={i}
                                  className={`page-item ${currentPage === i ? 'active' : ''}`}
                                >
                                  <button
                                    className='page-link text-muted'
                                    style={{
                                      backgroundColor:
                                        currentPage === i ? '#F4F9FF' : 'transparent',
                                      border: '1px solid #dee2e6',
                                      padding: '8px 12px',
                                      fontSize: '14px',
                                      minWidth: '40px',
                                      borderRadius: '6px',
                                    }}
                                    onClick={() => handlePageChange(i)}
                                  >
                                    {i}
                                  </button>
                                </li>
                              )
                            }

                            return pages
                          })()}

                          <li
                            className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}
                          >
                            <button
                              className='page-link text-muted'
                              style={{
                                backgroundColor: '#f8f9fa',
                                border: '1px solid #dee2e6',
                                padding: '8px 12px',
                                fontSize: '14px',
                                borderRadius: '6px',
                              }}
                              onClick={() => handlePageChange(currentPage + 1)}
                              disabled={currentPage === totalPages}
                            >
                              ›
                            </button>
                          </li>
                        </ul>
                      </nav>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {viewOpen && viewing && (
        <PmsJobDetailModal
          visible={viewOpen}
          onClose={() => {
            setViewOpen(false)
            setViewing(null)
          }}
          record={viewing}
          onRefresh={loadData}
          onViewDefect={(defectId) => openLinkedDefect(defectId)}
        />
      )}

      {completeOpen && completing && (
        <CompleteJobModal
          visible={completeOpen}
          onClose={() => {
            setCompleteOpen(false)
            setCompleting(null)
          }}
          job={completing}
          locked={Boolean(completing.masked)}
          onSuccess={() => {
            setCompleteOpen(false)
            setCompleting(null)
            loadData()
          }}
        />
      )}

      <CreateUnplannedJobModal
        visible={createJobOpen}
        onClose={() => {
          setCreateJobOpen(false)
          setCreateJobContext(null)
        }}
        onSuccess={() => {
          setCreateJobOpen(false)
          setCreateJobContext(null)
          loadData()
        }}
        vessels={filteredVessels}
        companies={companies}
        subcompanies={subcompanies}
        showCompanyFilters={roleId !== 4}
        isCrew={isCrew}
        initialVesselId={createJobContext?.initialVesselId}
        initialEquipmentId={createJobContext?.initialEquipmentId}
        initialSubComponentId={createJobContext?.initialSubComponentId}
        initialPlanLineId={createJobContext?.initialPlanLineId}
        lockPlanLine={createJobContext?.lockPlanLine}
      />

      <BatchJobActionModal
        visible={batchActionOpen}
        selectedJobIds={Array.from(selectedJobs)}
        onClose={() => {
          setBatchActionOpen(false)
        }}
        onSuccess={() => {
          setSelectedJobs(new Set())
          loadData()
        }}
      />

      <DeferJobModal
        visible={deferOpen}
        job={deferring}
        locked={Boolean(deferring?.masked)}
        onClose={() => {
          setDeferOpen(false)
          setDeferring(null)
        }}
        onSuccess={() => {
          setDeferOpen(false)
          setDeferring(null)
          loadData()
        }}
      />

      <PmsJobHistoryModal
        visible={historyOpen}
        job={historyJob}
        historyRecords={historyRecords}
        onClose={() => {
          setHistoryOpen(false)
          setHistoryJob(null)
          setHistoryRecords([])
        }}
      />

      {jobRequestModalOpen && (
        <div
          style={JOB_REQUEST_MODAL_STYLE}
          tabIndex={-1}
          onClick={(e) => {
            if (e.target === e.currentTarget) setJobRequestModalOpen(false)
          }}
        >
          <div
            className='bg-white text-dark rounded shadow-lg d-flex flex-column'
            style={{width: '1100px', maxWidth: '95vw', maxHeight: '90vh'}}
          >
            <div className='d-flex align-items-center justify-content-between border-bottom px-4 py-3'>
              <div>
                <h5 className='mb-1 text-dark'>Job Requests</h5>
                <div className='text-muted fs-7'>
                  Manage pending approvals and view approved logs
                </div>
              </div>
              <button
                type='button'
                className='btn-close'
                onClick={() => setJobRequestModalOpen(false)}
              />
            </div>

            <div className='px-4 py-3 border-bottom bg-light'>
              <div className='d-flex flex-column flex-xl-row gap-3 align-items-xl-center'>
                <div className='d-flex align-items-center gap-2 text-muted fs-7'>
                  <span>Vessel:</span>
                  <span className='badge badge-light-dark text-dark'>
                    {filters.vesselId
                      ? filteredVessels.find((v) => String(v.id) === filters.vesselId)
                          ?.fleet_name || 'Selected'
                      : 'All'}
                  </span>
                </div>

                <div className='ms-xl-auto d-flex flex-wrap gap-2 align-items-center'>
                  <div className='position-relative'>
                    <div
                      className='position-absolute ms-3'
                      style={{top: '50%', transform: 'translateY(-50%)'}}
                    >
                      <KTSVG
                        path='/media/icons/duotune/general/gen021.svg'
                        className='svg-icon-2'
                      />
                    </div>
                    <input
                      type='text'
                      className='form-control form-control-sm ps-10'
                      placeholder='Search defect, equipment, subcomponent, job...'
                      value={jobRequestSearch}
                      onChange={(e) => {
                        setJobRequestSearch(e.target.value)
                        setJobRequestPage(1)
                      }}
                      style={{minWidth: '260px'}}
                    />
                  </div>
                  <button
                    type='button'
                    className='btn btn-sm btn-light'
                    onClick={() => {
                      setJobRequestSearch('')
                      setJobRequestPage(1)
                    }}
                  >
                    Clear
                  </button>
                  <button
                    type='button'
                    className='btn btn-sm btn-light-primary'
                    onClick={() => loadJobRequestPage()}
                  >
                    Refresh
                  </button>
                </div>
              </div>
            </div>

            <div className='px-4 py-2 border-bottom'>
              <div className='d-flex flex-wrap align-items-center gap-2'>
                <div
                  className='btn-group btn-group-sm'
                  role='group'
                  aria-label='Job request status tabs'
                >
                  <button
                    type='button'
                    className={`btn ${jobRequestTab === 'PENDING' ? 'btn-primary' : 'btn-light'}`}
                    onClick={() => {
                      setJobRequestTab('PENDING')
                      setJobRequestPage(1)
                    }}
                  >
                    Pending
                    <span className='badge bg-white text-primary ms-2'>
                      {jobRequestPendingCount}
                    </span>
                  </button>
                  <button
                    type='button'
                    className={`btn ${jobRequestTab === 'ACCEPTED' ? 'btn-primary' : 'btn-light'}`}
                    onClick={() => {
                      setJobRequestTab('ACCEPTED')
                      setJobRequestPage(1)
                    }}
                  >
                    Approved
                  </button>
                  <button
                    type='button'
                    className={`btn ${jobRequestTab === 'REJECTED' ? 'btn-primary' : 'btn-light'}`}
                    onClick={() => {
                      setJobRequestTab('REJECTED')
                      setJobRequestPage(1)
                    }}
                  >
                    Rejected
                  </button>
                </div>
              </div>
            </div>

            <div className='flex-grow-1 overflow-auto px-4 py-3' style={{minHeight: 0}}>
              <div className='table-responsive'>
                <table className='table table-hover align-middle table-sm'>
                  <thead className='table-light'>
                    <tr>
                      <th style={{minWidth: '70px'}} className='text-center'>
                        Sr No
                      </th>
                      <th style={{minWidth: '130px'}}>Defect</th>
                      <th style={{minWidth: '240px'}}>Description</th>
                      {!isCrew && <th style={{minWidth: '150px'}}>Vessel</th>}
                      <th style={{minWidth: '200px'}}>Equipment</th>
                      <th style={{minWidth: '240px'}}>Subcomponents</th>
                      <th style={{minWidth: '260px'}}>Jobs</th>
                      <th style={{minWidth: '140px'}}>Date</th>
                      <th style={{minWidth: '140px'}}>Status</th>
                      {jobRequestTab === 'PENDING' && (
                        <th style={{minWidth: '160px'}} className='text-center'>
                          Actions
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {loadingJobRequests && (
                      <tr>
                        <td
                          colSpan={(jobRequestTab === 'PENDING' ? 10 : 9) - (isCrew ? 1 : 0)}
                          className='text-center py-4'
                        >
                          <div className='spinner-border spinner-border-sm text-primary'></div>
                          <span className='ms-2 text-muted'>Loading job requests...</span>
                        </td>
                      </tr>
                    )}
                    {!loadingJobRequests && jobRequestItems.length === 0 && (
                      <tr>
                        <td
                          colSpan={(jobRequestTab === 'PENDING' ? 10 : 9) - (isCrew ? 1 : 0)}
                          className='text-center py-4 text-muted'
                        >
                          No job requests found.
                        </td>
                      </tr>
                    )}
                    {!loadingJobRequests &&
                      jobRequestItems.map((request, index) => {
                        const requestId = request.id ?? -1
                        const serialNo = jobRequestStart + index
                        const jobGroups = (() => {
                          const groups = new Map<string, string[]>()
                          const ids =
                            request.jobPlanLineIds && request.jobPlanLineIds.length > 0
                              ? request.jobPlanLineIds
                              : request.jobPlanLineId
                              ? [request.jobPlanLineId]
                              : []
                          const codes = request.jobPlanLineJobCodes || []
                          const tasks = request.jobPlanLineTaskDescriptions || []
                          const subNames = request.jobPlanLineSubComponentNames || []

                          const push = (group: string, label: string) => {
                            if (!label || !label.trim()) return
                            const list = groups.get(group) ?? []
                            if (!list.includes(label)) {
                              list.push(label)
                              groups.set(group, list)
                            }
                          }

                          if (ids.length > 0) {
                            ids.forEach((id, idx) => {
                              const code = codes[idx]
                              const task = tasks[idx]
                              const label =
                                code && task
                                  ? `${code} - ${task}`
                                  : code || task || (id ? `Plan #${id}` : '')
                              const groupName =
                                subNames[idx] ||
                                request.subComponentName ||
                                'Unassigned Subcomponent'
                              push(groupName, label)
                            })
                          } else if (
                            request.jobPlanLineJobCode ||
                            request.jobPlanLineTaskDescription ||
                            request.jobPlanLineId
                          ) {
                            const label =
                              request.jobPlanLineJobCode && request.jobPlanLineTaskDescription
                                ? `${request.jobPlanLineJobCode} - ${request.jobPlanLineTaskDescription}`
                                : request.jobPlanLineJobCode ||
                                  request.jobPlanLineTaskDescription ||
                                  (request.jobPlanLineId ? `Plan #${request.jobPlanLineId}` : '')
                            const groupName = request.subComponentName || 'Unassigned Subcomponent'
                            push(groupName, label)
                          }

                          return Array.from(groups.entries()).map(([name, jobs]) => ({name, jobs}))
                        })()

                        const subcomponentBadgeLabels =
                          jobGroups.length > 0
                            ? jobGroups
                                .map((g) => g.name)
                                .filter((name) => name && name !== 'Unassigned Subcomponent')
                            : request.subComponentNames && request.subComponentNames.length > 0
                            ? request.subComponentNames.filter(Boolean)
                            : request.subComponentName
                            ? [request.subComponentName]
                            : []
                        const subcomponentsExpanded =
                          requestId > 0 && expandedSubcomponentRequests.has(requestId)
                        const jobsExpanded = requestId > 0 && expandedJobRequests.has(requestId)
                        const visibleSubcomponents = subcomponentsExpanded
                          ? subcomponentBadgeLabels
                          : subcomponentBadgeLabels.slice(0, 3)
                        const visibleJobGroups = jobsExpanded ? jobGroups : jobGroups.slice(0, 2)
                        const canToggleSubcomponents =
                          requestId > 0 && subcomponentBadgeLabels.length > 3
                        const canToggleJobs = requestId > 0 && jobGroups.length > 2
                        return (
                          <tr key={request.id}>
                            <td className='text-center text-muted'>{serialNo}</td>
                            <td>
                              <span className='badge badge-light-primary'>
                                {request.defectNumber || `D-${request.id}`}
                              </span>
                            </td>
                            <td>
                              {request.description ? (
                                <div
                                  className='text-dark fw-semibold text-truncate'
                                  style={{maxWidth: '280px'}}
                                  title={request.description}
                                >
                                  {request.description}
                                </div>
                              ) : (
                                <span className='text-muted'>No description</span>
                              )}
                            </td>
                            {!isCrew && (
                              <td>
                                <span className='text-dark'>{request.vesselName || 'N/A'}</span>
                              </td>
                            )}
                            <td>
                              {request.equipmentName ? (
                                <span className='badge badge-light-primary text-dark border border-primary'>
                                  {request.equipmentName}
                                </span>
                              ) : (
                                <span className='text-muted'>N/A</span>
                              )}
                            </td>
                            <td>
                              {subcomponentBadgeLabels.length > 0 ? (
                                <div className='d-flex flex-wrap gap-1'>
                                  {visibleSubcomponents.map((name, idx) => (
                                    <span
                                      key={`${name}-${idx}`}
                                      className='badge badge-light-info text-dark border border-info'
                                    >
                                      {name}
                                    </span>
                                  ))}
                                  {canToggleSubcomponents && (
                                    <button
                                      type='button'
                                      className='badge badge-light-secondary border-0'
                                      style={{cursor: 'pointer'}}
                                      onClick={() => toggleExpandedSubcomponents(requestId)}
                                    >
                                      {subcomponentsExpanded
                                        ? 'Show less'
                                        : `+${subcomponentBadgeLabels.length - 3} more`}
                                    </button>
                                  )}
                                </div>
                              ) : (
                                <span className='text-muted'>N/A</span>
                              )}
                            </td>
                            <td>
                              {jobGroups.length > 0 ? (
                                <div className='d-flex flex-column gap-2'>
                                  {visibleJobGroups.map((group) => (
                                    <div key={group.name} className='d-flex flex-column gap-1'>
                                      <span className='badge badge-light-info text-dark border border-info'>
                                        {group.name}
                                      </span>
                                      <div className='d-flex flex-wrap gap-1'>
                                        {group.jobs.map((label, idx) => (
                                          <span
                                            key={`${group.name}-${idx}`}
                                            className='badge badge-light-success text-dark border border-success'
                                          >
                                            {label}
                                          </span>
                                        ))}
                                      </div>
                                    </div>
                                  ))}
                                  {canToggleJobs && (
                                    <button
                                      type='button'
                                      className='badge badge-light-secondary border-0 align-self-start'
                                      style={{cursor: 'pointer'}}
                                      onClick={() => toggleExpandedJobs(requestId)}
                                    >
                                      {jobsExpanded ? 'Show less' : `+${jobGroups.length - 2} more`}
                                    </button>
                                  )}
                                </div>
                              ) : (
                                <span className='text-muted'>N/A</span>
                              )}
                            </td>
                            <td>
                              <div className='text-dark'>
                                {request.jobRequestCreatedAt
                                  ? new Date(request.jobRequestCreatedAt).toLocaleDateString(
                                      'en-GB',
                                      {
                                        day: '2-digit',
                                        month: 'short',
                                        year: 'numeric',
                                      }
                                    )
                                  : 'N/A'}
                              </div>
                              {request.jobRequestProcessedAt && jobRequestTab !== 'PENDING' && (
                                <div className='text-muted fs-8'>
                                  Processed:{' '}
                                  {new Date(request.jobRequestProcessedAt).toLocaleDateString(
                                    'en-GB',
                                    {
                                      day: '2-digit',
                                      month: 'short',
                                      year: 'numeric',
                                    }
                                  )}
                                </div>
                              )}
                            </td>
                            <td>
                              <span
                                className={`badge ${
                                  jobRequestTab === 'PENDING'
                                    ? 'badge-light-warning'
                                    : jobRequestTab === 'ACCEPTED'
                                    ? 'badge-light-success'
                                    : 'badge-light-danger'
                                }`}
                              >
                                {jobRequestTab === 'PENDING'
                                  ? 'Pending'
                                  : jobRequestTab === 'ACCEPTED'
                                  ? 'Approved'
                                  : 'Rejected'}
                              </span>
                              {request.jobRequestProcessedByName && jobRequestTab !== 'PENDING' && (
                                <div className='text-muted fs-8'>
                                  By {request.jobRequestProcessedByName}
                                </div>
                              )}
                            </td>
                            {jobRequestTab === 'PENDING' && (
                              <td className='text-center'>
                                <div className='d-flex gap-2 justify-content-center flex-wrap'>
                                  <button
                                    className='btn btn-sm btn-light-primary'
                                    onClick={() => {
                                      setViewDefectRecord(request)
                                      setViewDefectOpen(true)
                                    }}
                                    title='View defect details'
                                  >
                                    <KTSVG
                                      path='/media/icons/duotune/general/gen060.svg'
                                      className='svg-icon-3'
                                    />
                                    View
                                  </button>
                                  <button
                                    className='btn btn-sm btn-success'
                                    onClick={() => handleAcceptRequest(request)}
                                    title='Accept and create job'
                                  >
                                    <KTSVG
                                      path='/media/icons/duotune/general/gen055.svg'
                                      className='svg-icon-3'
                                    />
                                    Accept
                                  </button>
                                  <button
                                    className='btn btn-sm btn-danger'
                                    onClick={() => handleRejectRequest(request)}
                                    title='Reject job request'
                                  >
                                    <KTSVG
                                      path='/media/icons/duotune/general/gen027.svg'
                                      className='svg-icon-3'
                                    />
                                    Reject
                                  </button>
                                </div>
                              </td>
                            )}
                          </tr>
                        )
                      })}
                  </tbody>
                </table>
              </div>
            </div>

            {jobRequestTotalElements > 0 && (
              <div
                className='pagination-wrapper d-flex justify-content-between align-items-center py-3 border-top'
                style={{
                  position: 'static',
                  bottom: 0,
                  backgroundColor: '#fff',
                  zIndex: 10,
                }}
              >
                <div className='d-flex align-items-center'>
                  <span className='text-muted me-2'>Rows per page</span>
                  <SearchableSelect
                    className='form-select'
                    style={{
                      borderRadius: '20px',
                      width: '70px',
                      border: '1px solid #dee2e6',
                      fontSize: '14px',
                      padding: '4px 8px',
                    }}
                    value={jobRequestPageSize}
                    onChange={(e) => {
                      setJobRequestPageSize(Number(e.target.value))
                      setJobRequestPage(1)
                    }}
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                  </SearchableSelect>
                </div>

                <div className='d-flex align-items-center'>
                  <span className='text-muted me-3' style={{fontSize: '14px'}}>
                    Showing{' '}
                    <strong>
                      {jobRequestStart}-{jobRequestEnd}
                    </strong>{' '}
                    of <strong>{jobRequestTotalElements}</strong>
                  </span>

                  <nav>
                    <ul className='pagination pagination-sm mb-0' style={{gap: '2px'}}>
                      <li className={`page-item ${jobRequestPage === 1 ? 'disabled' : ''}`}>
                        <button
                          className='page-link text-muted'
                          style={{
                            backgroundColor: '#f8f9fa',
                            border: '1px solid #dee2e6',
                            padding: '8px 12px',
                            fontSize: '14px',
                            borderRadius: '6px',
                          }}
                          onClick={() => setJobRequestPage((prev) => Math.max(1, prev - 1))}
                          disabled={jobRequestPage === 1}
                        >
                          {'<'}
                        </button>
                      </li>

                      {(() => {
                        const pages = []
                        const showPages = 5
                        let startPage = Math.max(1, jobRequestPage - 2)
                        let endPage = Math.min(jobRequestTotalPages, startPage + showPages - 1)

                        if (endPage - startPage < showPages - 1) {
                          startPage = Math.max(1, endPage - showPages + 1)
                        }

                        for (let i = startPage; i <= endPage; i++) {
                          pages.push(
                            <li
                              key={i}
                              className={`page-item ${jobRequestPage === i ? 'active' : ''}`}
                            >
                              <button
                                className='page-link text-muted'
                                style={{
                                  backgroundColor: jobRequestPage === i ? '#F4F9FF' : 'transparent',
                                  border: '1px solid #dee2e6',
                                  padding: '8px 12px',
                                  fontSize: '14px',
                                  minWidth: '40px',
                                  borderRadius: '6px',
                                }}
                                onClick={() => setJobRequestPage(i)}
                              >
                                {i}
                              </button>
                            </li>
                          )
                        }

                        return pages
                      })()}

                      <li
                        className={`page-item ${
                          jobRequestPage === jobRequestTotalPages ? 'disabled' : ''
                        }`}
                      >
                        <button
                          className='page-link text-muted'
                          style={{
                            backgroundColor: '#f8f9fa',
                            border: '1px solid #dee2e6',
                            padding: '8px 12px',
                            fontSize: '14px',
                            borderRadius: '6px',
                          }}
                          onClick={() =>
                            setJobRequestPage((prev) => Math.min(jobRequestTotalPages, prev + 1))
                          }
                          disabled={jobRequestPage === jobRequestTotalPages}
                        >
                          {'>'}
                        </button>
                      </li>
                    </ul>
                  </nav>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {acceptModalOpen && (
        <AcceptJobRequestModal
          visible={acceptModalOpen}
          onClose={() => {
            setAcceptModalOpen(false)
            setSelectedRequest(null)
          }}
          onConfirm={handleAcceptConfirm}
          request={selectedRequest}
        />
      )}

      {rejectModalOpen && (
        <RejectJobRequestModal
          visible={rejectModalOpen}
          onClose={() => {
            setRejectModalOpen(false)
            setSelectedRequest(null)
          }}
          onConfirm={handleRejectConfirm}
          request={selectedRequest}
        />
      )}

      <ViewDefectModal
        visible={viewDefectOpen}
        onClose={() => {
          setViewDefectOpen(false)
          setViewDefectRecord(null)
        }}
        record={viewDefectRecord}
        onOpenAttachments={(rec) => {
          if (!rec?.id) return
          toast.info('Open QHSE Defects to manage attachments for this defect.')
        }}
        origin='job'
        wrapZIndex={2300}
      />

      <AddPlanModal
        visible={addPlanOpen}
        onClose={() => setAddPlanOpen(false)}
        onSubmit={handleCreatePlan}
        vessels={filteredVessels}
      />

      {plan?.id && plan.vesselId && (
        <AddPlanLineModal
          visible={addLineOpen}
          planId={plan.id}
          vesselId={plan.vesselId}
          onClose={() => setAddLineOpen(false)}
          onSuccess={async () => {
            setAddLineOpen(false)
            await loadData()
          }}
        />
      )}

      {editingLine && plan?.vesselId && (
        <EditPlanLineModal
          visible={editLineOpen}
          planLine={editingLine}
          vesselId={plan.vesselId}
          locked={editingLineLocked}
          onClose={() => {
            setEditLineOpen(false)
            setEditingLine(null)
            setEditingLineLocked(false)
          }}
          onSuccess={async () => {
            setEditLineOpen(false)
            setEditingLine(null)
            setEditingLineLocked(false)
            await loadData()
          }}
        />
      )}

      <JobMaskingModal
        visible={jobMaskingOpen}
        vesselId={filters.vesselId ? Number(filters.vesselId) : null}
        vesselName={
          filters.vesselId
            ? filteredVessels.find((v) => String(v.id) === filters.vesselId)?.fleet_name ||
              filteredVessels.find((v) => String(v.id) === filters.vesselId)?.name ||
              `Vessel ${filters.vesselId}`
            : null
        }
        onClose={() => setJobMaskingOpen(false)}
        onSuccess={loadData}
      />

      <ToastContainer
        position='top-center'
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        style={{top: '5rem'}}
      />
    </div>
  )
}

export default PmsJobListPage
